import { DecimalPipe, NgTemplateOutlet } from '@angular/common';
import { Component, HostListener, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { ApiClientService } from '../../core/api-client.service';
import { AuthService } from '../../core/auth.service';
import { I18nService } from '../../core/i18n.service';
import { SeoService } from '../../core/seo.service';
import { PageDataService } from '../../core/page-data.service';
import { SiteConfigService } from '../../core/site-config.service';
import { MarkdownHtmlPipe } from '../../shared/markdown-html.pipe';
import { ResponsiveImageComponent } from '../../shared/responsive-image.component';
import { FortuneListing, FortuneListingDetail, FortunePaymentGateway, FortuneService } from './fortune.service';
import { CommerceCartService } from '../../core/commerce-cart.service';
import { AnalyticsService } from '../../core/analytics.service';
import { AuthWidgetComponent } from '../auth/auth-widget.component';
import { FavoriteCollectionWidgetComponent } from '../collections/favorite-collection-widget.component';
import { FormUploadService } from '../forms/form-upload.service';
import { FormUploadFieldComponent } from '../forms/form-upload-field.component';

@Component({
  selector: 'app-fortune-detail-widget',
  imports: [AuthWidgetComponent, DecimalPipe, FormsModule, MarkdownHtmlPipe, NgTemplateOutlet, ResponsiveImageComponent, RouterLink, FavoriteCollectionWidgetComponent, FormUploadFieldComponent],
  template: `
    <section class="fortune-widget" [attr.data-product-layout]="themeString('layout','gallery_left')" [attr.data-gallery-style]="themeString('gallery_style','portrait')" [attr.data-desktop-gallery-layout]="themeString('desktop_gallery_layout','standard')" [attr.data-option-style]="themeString('option_style','cards')" [attr.data-form-style]="themeString('form_style','panel')">
      @if (loginPromptOpen() && !auth.loggedIn()) {
        <app-auth-widget [autoOpen]="true" />
      }
      @if (creditTopUpOpen()) {
        <div class="credit-topup-overlay" role="dialog" aria-modal="true" aria-label="Kredi yükleme">
          <section class="credit-topup-window">
            <header class="credit-topup-window__head"><strong>Kredi yükle</strong><button type="button" class="ghost" (click)="closeCreditTopUp()">Kapat ×</button></header>
            <div class="credit-topup-frame-wrap">
              @if (!creditTopUpFrameReady()) { <div class="credit-topup-loading">Kredi yükleme ekranı hazırlanıyor...</div> }
              <iframe [src]="creditTopUpUrl()" title="Kredi yükleme" (load)="creditTopUpFrameReady.set(true)"></iframe>
            </div>
          </section>
        </div>
      }
      @if (completedOrderID() > 0) {
        <div style="align-items:center;background:color-mix(in srgb,#140918 78%,transparent);display:flex;inset:0;justify-content:center;padding:20px;position:fixed;z-index:140" role="status">
          <section style="background:var(--surface);border:1px solid color-mix(in srgb,var(--accent) 35%,var(--border));border-radius:30px;box-shadow:0 30px 90px color-mix(in srgb,#000 45%,transparent);display:grid;gap:16px;max-width:520px;padding:clamp(24px,5vw,46px);text-align:center;width:100%">
            <p class="eyebrow">TAMAMLANDI</p><h2>Falın gönderildi.</h2>
            <a class="primary" style="display:inline-flex;justify-content:center;margin:0;text-decoration:none;justify-self:center" [routerLink]="i18n.routePath('orderDetail', { slug: completedOrderID() })">Hemen görüntüle</a>
            <p>{{ successCountdown() }} saniye sonra Fallarım sayfasına yönlendiriliyorsun.</p>
          </section>
        </div>
      }
      @if (zoomOpen()) {
        <div class="image-lightbox" role="dialog" aria-modal="true" aria-label="Product image" (click)="closeZoom()" (pointermove)="zoomPointerMove($event)" (pointerup)="zoomPointerUp($event)" (pointercancel)="zoomPointerUp($event)">
          <button type="button" class="image-lightbox__close" aria-label="Close image" (click)="zoomOpen.set(false)">×</button>
          <div class="image-lightbox__tools" (click)="$event.stopPropagation()">
            <button type="button" (click)="zoomOut()" aria-label="Zoom out">−</button>
            <span>{{ zoomScale() * 100 | number: '1.0-0' }}%</span>
            <button type="button" (click)="zoomIn()" aria-label="Zoom in">+</button>
          </div>
          <div class="image-lightbox__viewport" (click)="$event.stopPropagation()" (pointerdown)="zoomPointerDown($event)">
            @if (zoomImageUrl(); as zoomImage) {
              <img class="lightbox-image" [src]="zoomImage" [alt]="detail()?.title || ''" draggable="false" [style.transform]="zoomTransform()" />
            }
          </div>
        </div>
      }
      @if (loading()) {
        <section class="page-card">
          <p class="eyebrow">{{ commerce() ? copy().product : copy().reading }}</p>
          <h1>{{ commerce() ? copy().productLoading : copy().readingLoading }}</h1>
        </section>
      } @else if (detail(); as item) {
        @if (showThemeBreadcrumb()) { <nav class="fortune-breadcrumb" aria-label="Breadcrumb">
          <a [routerLink]="i18n.routePath('home')">{{ i18n.t('home') }}</a>
          @for (category of item.categoryPath ?? []; track category.slug ?? category.title) {
            <span>·</span>
            @if (category.slug) {
              <a [routerLink]="i18n.routePath('category', { slug: category.slug })">{{ category.title }}</a>
            } @else {
              <strong>{{ category.title }}</strong>
            }
          }
          <span>·</span>
          <strong>{{ item.title }}</strong>
        </nav> }
          <div class="detail-hero" [class.detail-hero--no-media]="!detailMediaVisible(item)">
          @if (detailMediaVisible(item)) {
          <div class="detail-media">
            @if (themeDetailImage(item); as detailImage) {
              <button type="button" class="detail-image-trigger" aria-label="Zoom image" (mousemove)="previewImageOnPointer($event)" (click)="openZoom()">
                <app-responsive-image
                  [url]="detailImage"
                  [mediaSizes]="themeDetailImageSizes(item)"
                  [alt]="themeString('media_source','auto') === 'seller' ? item.sellerName : item.title"
                  displaySizes="(max-width: 900px) 92vw, 540px"
                  fit="contain"
                  [priority]="true"
                />
                @if (commerce()) { <span class="zoom-hint">⌕ Zoom</span> }
              </button>
            }
            @if (commerce() && item.images.length > 1) {
              <div class="gallery-thumbs">
                @for (image of item.images; track $index) {
                  @if (image.url) {
                    <button type="button" [class.active]="activeImageIndex() === $index" (mouseenter)="activeImageIndex.set($index)" (focus)="activeImageIndex.set($index)" (click)="activeImageIndex.set($index)">
                      <app-responsive-image [url]="image.url" [mediaSizes]="image.sizes" [alt]="galleryImageAlt(item.title, $index + 1)" [displaySizes]="galleryThumbDisplaySizes()" />
                    </button>
                  }
                }
              </div>
            }
          </div>
          }
          <div class="detail-copy">
            @if (showThemeTitle()) { <h1>{{ item.title }}</h1> }
            @if (showThemeSeller() && (item.sellerName || item.sellerShopName)) {
              <a class="seller-line" [routerLink]="sellerLink() || i18n.routePath('sellers')">{{ themeSellerLabel(item) }}</a>
              <div class="reader-trust-strip">
                <span class="reader-presence" [attr.data-presence]="item.online ? 'online' : (item.presenceStatus || item.liveStatus || 'offline')"><i></i>{{ readerPresenceLabel(item) }}</span>
                @if (item.rankTitle) { <span class="reader-rank">{{ item.rankTitle }}</span> }
                @if (item.rating > 0) { <span class="reader-rating">★★★★★ <b>{{ item.rating | number: '1.1-2' }}</b></span> }
                <span>{{ item.reviewCount }} {{ copy().reviews }}</span>
                @if (item.orderCount > 0) { <span>{{ item.orderCount }} fal</span> }
              </div>
              @if ((item.pendingFortunes ?? 0) > 0) { <p class="reader-pending">⌛ {{ item.pendingFortunes }} fal yorumlanmayı bekliyor</p> }
              @if (item.highlightText) { <p class="reader-highlight">{{ item.highlightText }}</p> }
            }
            @if (showThemePrice()) { <div class="price-line">@if (campaignPrice() !== null) { <del style="color:var(--muted);font-size:.62em;font-weight:700;margin-right:.28em">{{ totalPrice() | number: '1.0-2' }} {{ priceUnit(item.currency) }}</del><strong style="color:var(--accent)">{{ campaignPrice() | number: '1.0-2' }} {{ priceUnit(item.currency) }}</strong> } @else { <strong style="color:var(--accent)">{{ totalPrice() | number: '1.0-2' }} {{ priceUnit(item.currency) }}</strong> } @if (item.campaign) { <span style="flex-basis:100%"><span class="campaign-badge">{{ item.campaign.title || 'Campaign' }}</span></span> }</div> }
            @if (returnDays(item); as days) {
              <p style="color:var(--accent);font-weight:850;margin:-8px 0 16px">{{ days }} {{ copy().returnDays }}</p>
            }
            @if (showThemeFavorite() && favoritesEnabled()) { <div class="detail-favorite"><app-favorite-collection [listingID]="item.id" [listingSlug]="item.slug" /><span>{{ i18n.collectionT('favoriteAdd') }}</span></div> }
            <ng-container *ngTemplateOutlet="productOptionsForm"></ng-container>
          </div>
        </div>

        <ng-template #productOptionsForm>
          <form class="detail-form">
            @if (item.options.length) {
              <section class="form-section">
                <p class="eyebrow">{{ copy().options }}</p>
                @for (option of item.options; track option.id ?? option.name; let optionIndex = $index) {
                  <div class="option-group">
                    <h2>{{ option.name || option.title || copy().option }}</h2>
                    @if (sizeDecisionNote(option)) {
                      <p class="option-decision-note">{{ sizeDecisionNote(option) }} <a [routerLink]="i18n.routePath('support')" [queryParams]="{product:item.slug,topic:'dimensions'}">{{ sizeHelpLabel() }}</a></p>
                    }
                    <div class="option-list">
                      @for (sub of option.subOptions; track sub.id ?? sub.title; let subIndex = $index) {
                        @if (sub.active !== false) {
                        <label class="option-card">
                          <input
                            type="radio"
                            [name]="'option_' + (option.id || optionIndex)"
                            [value]="sub.id"
                            [checked]="selectedOptions()[option.id || optionIndex] === sub.id"
                            (change)="selectOption(option.id || optionIndex, sub.id)"
                          />
                          <span>
                            <strong>{{ sub.title || sub.name || copy().subOption }}</strong>
                            @if (sub.info) {
                              <small>{{ sub.info }}</small>
                            }
                          </span>
                          @if (option.processType === 'variation' && sub.price !== null && sub.price !== undefined) {
                            <span style="align-items:flex-end;display:flex;flex-direction:column;gap:3px;white-space:nowrap">@if (hasOptionDiscount(sub.price)) { <del style="color:var(--muted);font-size:.82em;font-weight:700">{{ sub.price | number: '1.0-2' }} {{ priceUnit(item.currency) }}</del> }<b style="color:var(--accent)">{{ discountedOptionPrice(sub.price) | number: '1.0-2' }} {{ priceUnit(item.currency) }}</b></span>
                          } @else if ((sub.price ?? 0) > 0) {
                            <span style="align-items:flex-end;display:flex;flex-direction:column;gap:3px;white-space:nowrap">@if (hasOptionDiscount(sub.price)) { <del style="color:var(--muted);font-size:.82em;font-weight:700">+{{ sub.price | number: '1.0-2' }} {{ priceUnit(item.currency) }}</del> }<b style="color:var(--accent)">+{{ discountedOptionPrice(sub.price) | number: '1.0-2' }} {{ priceUnit(item.currency) }}</b></span>
                          }
                        </label>
                        }
                      }
                    </div>
                  </div>
                }
              </section>
            }

            @if (item.customization.length) {
              <section class="form-section">
                <p class="eyebrow">{{ commerce() ? copy().customizations : copy().readingDetails }}</p>
                <div class="custom-grid">
                  @for (field of item.customization; track field.id) {
                    <div class="custom-field" [class.wide]="isWideField(field.type)">
                      <label [attr.for]="field.formKey">{{ field.title }} @if (field.required) { <span>*</span> }</label>
                      @if (field.type === 'textarea') {
                        <textarea rows="4" [id]="field.formKey" [name]="field.formKey" [ngModel]="customValue(field)" (ngModelChange)="setCustomValue(field, $event)" [required]="field.required"></textarea>
                      } @else if (field.type === 'radio') {
                        <div class="radio-pills">
                          @for (option of field.options; track option.value) {
                            <button type="button" [class.selected]="customValue(field) === option.value" (click)="setCustomValue(field, option.value)">
                              {{ option.title }}
                            </button>
                          }
                        </div>
                      } @else if (field.type === 'select' || field.type === 'choice') {
                        <select [id]="field.formKey" [name]="field.formKey" [ngModel]="customValue(field)" (ngModelChange)="setCustomValue(field, $event)" [required]="field.required">
                          <option value="">{{ copy().select }}</option>
                          @for (option of field.options; track option.value) {
                            <option [value]="option.value">{{ option.title }}</option>
                          }
                        </select>
                      } @else if (field.type === 'checkbox') {
                        <div class="checkbox-list">
                          @for (option of field.options; track option.value) {
                            <label>
                              <input type="checkbox" [checked]="checkboxValue(field, option.value)" (change)="toggleCheckboxValue(field, option.value, $event)" />
                              {{ option.title }}
                            </label>
                          }
                        </div>
                      } @else if (field.type === 'switch') {
                        <input type="checkbox" [id]="field.formKey" [name]="field.formKey" [ngModel]="!!customValue(field)" (ngModelChange)="setCustomValue(field, $event)" />
                      } @else if (field.type === 'date') {
                        <input type="date" [id]="field.formKey" [name]="field.formKey" [ngModel]="customValue(field)" (ngModelChange)="setCustomValue(field, $event)" [required]="field.required" />
                      } @else if (field.type === 'datetime') {
                        <input type="datetime-local" [id]="field.formKey" [name]="field.formKey" [ngModel]="customValue(field)" (ngModelChange)="setCustomValue(field, $event)" [required]="field.required" />
                      } @else if (field.type === 'time') {
                        <input type="time" [id]="field.formKey" [name]="field.formKey" [ngModel]="customValue(field)" (ngModelChange)="setCustomValue(field, $event)" [required]="field.required" />
                      } @else if (field.type === 'email') {
                        <input type="email" [id]="field.formKey" [name]="field.formKey" [ngModel]="customValue(field)" (ngModelChange)="setCustomValue(field, $event)" [required]="field.required" />
                      } @else if (field.type === 'phone') {
                        <input type="tel" [id]="field.formKey" [name]="field.formKey" [ngModel]="customValue(field)" (ngModelChange)="setCustomValue(field, $event)" [required]="field.required" />
                      } @else if (isUploadField(field.type)) {
                        <app-form-upload-field
                          [fieldKey]="field.formKey"
                          [fieldType]="field.type"
                          [endpoint]="uploadUrl(field)"
                          [minFiles]="minFiles(field)"
                          [maxFiles]="maxFiles(field)"
                          [value]="uploadedFiles(field)"
                          [uploading]="!!uploadingFields()[field.formKey]"
                          (uploadState)="setUploadingField(field.formKey, $event)"
                          (valueChange)="setCustomValue(field, $event)"
                          (uploadError)="orderError.set($event)"
                        />
                      } @else {
                        <input type="text" [id]="field.formKey" [name]="field.formKey" [ngModel]="customValue(field)" (ngModelChange)="setCustomValue(field, $event)" [required]="field.required" />
                      }
                      @if (field.description) {
                        <small class="field-help">{{ field.description }}</small>
                      }
                    </div>
                  }
                </div>
              </section>
            }
            @if (commerce() && (isPhysicalProduct(item) || shippingOptions(item).length)) {
              <section class="form-section purchase-info shipping-section">
                <p class="eyebrow">{{ copy().shipping }}</p>
                @if (item.shippingUnavailable) {
                  <p class="purchase-info__line" style="color:#ff7777;font-weight:850">{{ copy().shippingUnavailable }}</p>
                  <button type="button" class="ghost" style="margin-top:10px" (click)="openLocaleSelector()">{{ copy().changeCountry }}</button>
                } @else { @for (shipping of shippingOptions(item); track $index) {
                  <div class="shipping-schedule" style="background:color-mix(in srgb,var(--surface) 86%,var(--accent));border:1px solid var(--border);border-radius:18px;margin-top:12px;padding:16px">
                    <strong style="display:block;font-size:1.05em">{{ shippingName(shipping) }}</strong>
                    <div style="display:grid;gap:4px;margin-top:8px">
                      <p class="purchase-info__line" style="margin:0"><strong>{{ copy().shippingFee }}</strong> {{ shippingPrice(shipping) }}</p>
                      <p class="purchase-info__line" style="margin:0;color:var(--accent);font-weight:900"><strong>{{ copy().deliveryEstimate }}</strong> {{ deliveryWindow(shipping) }}</p>
                    </div>
                  </div>
                } @empty {
                  <p class="purchase-info__line">{{ copy().shippingAvailable }}</p>
                } }
              </section>
            }
            @if (!commerce()) {
              <section class="purchase-credit-panel" aria-label="Fal siparişi özeti">
                <div>
                  <p class="eyebrow">{{ copy().summary }}</p>
                  @if (showThemeReaderName()) { <strong>{{ item.sellerName }}{{ copy().willInterpret }}</strong> }
                </div>
                @if (auth.loggedIn() && creditPaymentMode(item)) {
                  <div class="credit-order-summary">
                    <span>Fal ücreti</span><strong>{{ fortuneCreditCost() | number: '1.0-2' }} kredi</strong>
                    <span>Kredi bakiyen</span><strong>{{ (auth.creditBalance() ?? 0) | number: '1.0-2' }} kredi</strong>
                  </div>
                }
              </section>
              @if (orderError()) {
                <div class="order-error">{{ orderError() }}</div>
              }
              @if (orderSuccess()) {
                <div class="order-success">{{ orderSuccess() }}</div>
              }
            }
            <section class="form-section purchase-actions" style="border-top:1px solid var(--border);margin-top:18px;padding-top:18px">
              @if (commerce()) {
                <button type="button" class="primary" [disabled]="ordering()" (click)="sendFortune(item)">
                  {{ ordering() ? copy().preparingReading : commerceBuyLabel() }}
                </button>
              } @else {
                @for (gateway of paymentGateways(item); track gateway.id) {
                  @if (creditNeedsTopUp(item, gateway)) {
                    <button type="button" class="primary" [disabled]="ordering()" (click)="goToCreditTopUp()">Kredin yetersiz. Hemen Kredi al</button>
                  } @else {
                    <button type="button" class="primary" [disabled]="ordering()" (click)="payWithGateway(item, gateway)">
                      {{ ordering() ? copy().preparingReading : paymentGatewayLabel(item, gateway) }}
                    </button>
                  }
                }
                @if (!paymentGateways(item).length) {
                  <button type="button" class="primary" [disabled]="ordering()" (click)="sendFortune(item)">
                    {{ ordering() ? copy().preparingReading : sendButtonLabel(item) }}
                  </button>
                }
                @if (auth.loggedIn() && paymentGateways(item).length && !hasFastPayment(item)) {
                  <button type="button" class="quick-buy" [disabled]="ordering()" (click)="sendFortune(item)">
                    {{ ordering() ? copy().preparingReading : sendButtonLabel(item) }}
                  </button>
                }
              }
              @if (commerce() && isPhysicalProduct(item) && returnAccepted(item)) {
                <p style="color:#65d391;font-weight:850;margin:10px 0 0">{{ returnAccepted(item) }} {{ copy().returnDays }}</p>
              }
              <small>{{ commerce() ? copy().checkoutNote : copy().readingCheckoutNote }}</small>
            </section>
          </form>
        </ng-template>

        <div class="detail-layout">
          <section class="detail-description description form-section">
            <p class="eyebrow">{{ commerce() ? copy().product : copy().reading }}</p>
            <div class="description-content" style="overflow:hidden" [style.max-height]="descriptionExpanded() ? 'none' : '13.5em'" [style.webkitMaskImage]="descriptionExpanded() ? 'none' : 'linear-gradient(#000 72%, transparent)'" [style.maskImage]="descriptionExpanded() ? 'none' : 'linear-gradient(#000 72%, transparent)'" [innerHTML]="item.description | markdownHtml"></div>
            @if (item.description) {
              <button type="button" class="description-toggle" style="background:transparent;border:0;color:var(--accent);cursor:pointer;display:block;font:inherit;font-weight:900;margin:12px auto 0;padding:8px 14px;text-decoration:underline" (click)="toggleDescription()">
                {{ descriptionExpanded() ? copy().showLess : copy().showMore }}
              </button>
            }
          </section>
          @if (commerce()) {
          <aside class="checkout-card">
            <p class="eyebrow">{{ copy().summary }}</p>
            <h2 style="align-items:baseline;display:flex;flex-wrap:wrap;gap:.2em">@if (campaignPrice() !== null) { <del style="color:var(--muted);font-size:.62em;font-weight:700">{{ totalPrice() | number: '1.0-2' }} {{ priceUnit(item.currency) }}</del><strong style="color:var(--accent)">{{ campaignPrice() | number: '1.0-2' }} {{ priceUnit(item.currency) }}</strong> } @else { <strong style="color:var(--accent)">{{ totalPrice() | number: '1.0-2' }} {{ priceUnit(item.currency) }}</strong> }</h2>
            <p>{{ commerce() ? sellerLabel(item) + copy().safePurchase : item.sellerName + copy().willInterpret }}</p>
            @if (policyEntries(item).length) {
              <section class="purchase-info purchase-policies" style="border-top:1px solid var(--border);margin-top:18px;padding-top:14px">
                <p class="eyebrow">{{ copy().policies }}</p>
                @if (cancellationAllowed(item)) {
                  <p class="purchase-info__line" style="color:#65d391!important;font-weight:850">{{ copy().cancellationAllowed }}</p>
                } @else if (cancellationAllowed(item) === false) {
                  <p class="purchase-info__line" style="color:#ff7777!important;font-weight:850">{{ copy().cancellationDenied }}</p>
                }
                @if (returnShippingCustomer(item); as payer) {
                  <p class="purchase-info__line">{{ payer === 'customer' ? copy().returnShippingCustomer : copy().returnShippingSeller }}</p>
                }
                @if (!isPhysicalProduct(item) && downloadLimit(item); as limit) {
                  <p class="purchase-info__line">{{ copy().downloadLimit }}: {{ limit }}</p>
                }
                @for (policy of additionalPolicyEntries(item); track policy.key) {
                  <p class="purchase-info__line"><strong>{{ policy.label }}:</strong> {{ policy.value }}</p>
                }
              </section>
            }
            @if (orderError()) {
              <div class="order-error">{{ orderError() }}</div>
            }
            @if (orderSuccess()) {
              <div class="order-success">{{ orderSuccess() }}</div>
            }
            @if (sellerMessagingEnabled()) {
              <section class="seller-message-box">
                <p class="eyebrow">{{ copy().askSeller }}</p>
                <button type="button" class="ghost" (click)="openSellerConversation(item)">
                  {{ auth.loggedIn() ? copy().openMessages : copy().loginToMessage }}
                </button>
              </section>
            }
          </aside>
          } @else {
          <aside class="reader-summary-card" [class.reader-summary-card--credit]="emphasizeThemeCredit()">
            <div class="reader-summary-card__profile">
              @if (showThemeReaderImage()) {
              <div class="seller-spotlight__avatar">
                @if (item.sellerImage) {
                  <app-responsive-image [url]="item.sellerImage" [mediaSizes]="item.sellerImageSizes" [alt]="item.sellerName" displaySizes="72px" />
                } @else {
                  <span>{{ initials(item.sellerName || '') }}</span>
                }
              </div>
              }
              <div class="reader-summary-card__copy">
                <p class="eyebrow">{{ copy().reader }}</p>
                @if (showThemeReaderName()) { <h2>{{ sellerLabel(item) }}</h2> }
                @if (showThemeReaderRating()) { <p>{{ item.rating | number: '1.1-2' }} {{ copy().rating }} · {{ item.reviewCount }} {{ copy().reviews }}</p> }
              </div>
            </div>
            <div class="reader-summary-card__actions">
              @if (sellerLink()) {
                <a class="ghost" [routerLink]="sellerLink()!">{{ copy().readerPage }}</a>
              }
              @if (sellerMessagingEnabled()) {
                <button type="button" class="primary" (click)="openSellerConversation(item)">{{ copy().sendMessage }}</button>
              }
            </div>
          </aside>
          }
        </div>

        @if (commerce() && site.config().capabilities.marketplace) {
        <section class="seller-spotlight">
          <div class="seller-spotlight__card">
            <div class="seller-spotlight__avatar">
              @if (item.sellerImage) {
                <app-responsive-image [url]="item.sellerImage" [mediaSizes]="item.sellerImageSizes" [alt]="item.sellerName" displaySizes="92px" />
              } @else {
                <span>{{ initials(item.sellerName || item.sellerShopName || '') }}</span>
              }
            </div>
            <div class="seller-spotlight__copy">
              <p class="eyebrow">{{ commerce() ? copy().store : copy().reader }}</p>
              <h2>{{ sellerLabel(item) }}</h2>
              <p>{{ sellerLabel(item) }} · {{ item.rating | number: '1.1-2' }} {{ copy().rating }} · {{ item.reviewCount }} {{ copy().reviews }}</p>
            </div>
            <div class="seller-spotlight__actions">
              @if (sellerLink() && (!commerce() || site.config().capabilities.marketplace)) {
                <a class="ghost" [routerLink]="sellerLink()!">{{ commerce() ? copy().visitStore : copy().readerPage }}</a>
              }
              @if (sellerMessagingEnabled()) {
              <button type="button" class="primary" (click)="openSellerConversation(item)">{{ copy().sendMessage }}</button>
              }
            </div>
          </div>
        </section>
        }

        @if (commerce() ? item.reviews.length : (item.reviews.length || item.sellerReviews.length)) {
          <section class="product-reviews">
            @if (commerce()) {
              <div class="section-head">
                <div><p class="eyebrow">{{ copy().reviews }}</p><h2>{{ reviewsHeading() }}</h2></div>
              </div>
            } @else {
              <h2 class="reviews-compact-heading">{{ copy().reviews }}</h2>
            }
            @if (!commerce()) {
              <div class="review-tabs" role="tablist" [attr.aria-label]="copy().reviews">
                <button type="button" role="tab" [class.active]="reviewTab() === 'listing'" (click)="reviewTab.set('listing'); reviewPage.set(1)">{{ copy().readingReviews }}</button>
                <button type="button" role="tab" [class.active]="reviewTab() === 'seller'" (click)="reviewTab.set('seller'); reviewPage.set(1)">{{ copy().readerReviews }}</button>
              </div>
            }
            <div class="product-reviews__grid">
              @for (review of reviewItems(item); track review['id']) {
                <blockquote [class.editorial]="isEditorialReview(review)">
                  <header><strong>{{ reviewDisplayName(review) }}</strong><span>{{ reviewStars(review) }}</span></header>
                  @if (!commerce()) { <small class="review-context">{{ item.categoryTitle }} · {{ item.title }}</small> }
                  <p>{{ review['comment'] || review['review'] || review['content'] }}</p>
                  @if (isEditorialReview(review)) { <small>{{ editorialLabel() }}</small> }
                </blockquote>
              }
              @if (reviewItems(item).length > reviewPage() * reviewPageSize()) {
                <button type="button" class="review-more" (click)="loadMoreReviews()">Daha fazla yorum göster</button>
              }
            </div>
          </section>
        }

        @if (faqItems(item).length) {
          <section class="product-faqs page-card" style="margin-top:24px">
            <p class="eyebrow">{{ copy().faq }}</p>
            @for (faq of faqItems(item); track faq['id'] ?? $index) {
              <details style="border-bottom:1px solid var(--border);padding:14px 0">
                <summary style="cursor:pointer;font-weight:850">{{ faq['title'] || faq['question'] || faq['name'] }}</summary>
                <div style="margin-top:10px" [innerHTML]="faqText(faq) | markdownHtml"></div>
              </details>
            }
          </section>
        }

        @if (sameCategoryListings().length || sameSellerListings().length) {
          <section class="related-sections">
            @if (sameCategoryListings().length) {
              <div class="related-block">
                <div class="section-head">
                  <div>
                    <p class="eyebrow">{{ commerce() ? copy().similarProducts : copy().similarReadings }}</p>
                    <h2>{{ copy().sameCategory }}</h2>
                  </div>
                </div>
                <div class="listing-grid related-grid">
                  @for (listing of sameCategoryListings(); track listing.id) {
                    <a class="listing-card" [routerLink]="i18n.routePath('listing', { slug: listing.slug })">
                      @if (commerce() && listing.imageUrl) { <div class="cover"><app-responsive-image [url]="listing.imageUrl" [mediaSizes]="listing.imageSizes" [alt]="listing.title" displaySizes="(max-width: 900px) 46vw, 260px" /></div> }
                      <div class="card-body">
                        <p class="seller-name">{{ sellerLabel(listing) }}</p>
                        <h3>{{ listing.title }}</h3>
                        <strong>{{ listing.price | number: '1.0-2' }} {{ listing.currency }}</strong>
                      </div>
                    </a>
                  }
                </div>
              </div>
            }
            @if (sameSellerListings().length) {
              <div class="related-block">
                <div class="section-head">
                  <div>
                    <p class="eyebrow">{{ copy().sameSeller }}</p>
                    <h2>{{ item.sellerName }} · {{ copy().otherOffers }}</h2>
                  </div>
                </div>
                <div class="listing-grid related-grid">
                  @for (listing of sameSellerListings(); track listing.id) {
                    <a class="listing-card" [routerLink]="i18n.routePath('listing', { slug: listing.slug })">
                      @if (commerce() && listing.imageUrl) { <div class="cover"><app-responsive-image [url]="listing.imageUrl" [mediaSizes]="listing.imageSizes" [alt]="listing.title" displaySizes="(max-width: 900px) 46vw, 260px" /></div> }
                      <div class="card-body">
                        <p class="seller-name">{{ listing.categoryTitle }}</p>
                        <h3>{{ listing.title }}</h3>
                        <strong>{{ listing.price | number: '1.0-2' }} {{ listing.currency }}</strong>
                      </div>
                    </a>
                  }
                </div>
              </div>
            }
          </section>
        }

        @if (sellerMessageDrawerOpen()) {
          <div class="seller-drawer-backdrop" (click)="sellerMessageDrawerOpen.set(false)"></div>
          <aside class="seller-message-drawer">
            <header class="seller-message-drawer__head">
              <div>
                @if (sellerConversationOpen()) {
                  <button type="button" class="seller-chat-back" (click)="showSellerThreads()">←</button>
                }
                <div>
                  <h2>{{ sellerConversationOpen() ? (activeSellerName() || item.sellerName) : 'Tüm mesajlar' }}</h2>
                </div>
              </div>
              <button type="button" class="seller-chat-close" (click)="sellerMessageDrawerOpen.set(false)">×</button>
            </header>
            @if (!sellerConversationOpen()) {
              <div class="seller-thread-list">
                @for (thread of sellerThreadList(); track thread.id) {
                  <button type="button" class="seller-thread-row" (click)="openSellerThread(thread.id)">
                    <span class="seller-thread-avatar">
                      @if (thread.sellerAvatar) {
                        <app-responsive-image [url]="thread.sellerAvatar" [mediaSizes]="thread.sellerAvatarSizes" [alt]="thread.sellerName || 'Falcı'" displaySizes="52px" />
                      } @else {
                        {{ initials(thread.sellerName || '') }}
                      }
                    </span>
                    <span class="seller-thread-copy">
                      <strong>{{ thread.sellerName || 'Falcı' }}</strong>
                      <span>{{ thread.lastMessage || 'Henüz mesaj yok.' }}</span>
                      <small>{{ thread.lastMessageAt || 'Tarih yok' }} · {{ thread.threadStatusLabel || (thread.hasUnread ? 'Yeni mesaj' : 'Goruldu') }}</small>
                    </span>
                    @if (thread.hasUnread) { <i class="seller-thread-unread" aria-label="Okunmamış mesaj"></i> }
                  </button>
                } @empty {
                  <div class="seller-thread-empty">Henüz mesajlaşman yok.</div>
                }
              </div>
            } @else {
              @if (messageNotice()) {
                <p class="seller-message-notice" role="status">{{ messageNotice() }}</p>
              }
              <div class="seller-message-history" aria-live="polite">
                @for (message of sellerConversation(); track $index) {
                  <div class="seller-message-bubble" [class.mine]="message.mine">
                    @if (message.listingSlug && message.listingTitle) {
                      <a class="seller-message-listing" [routerLink]="i18n.routePath('listing', { slug: message.listingSlug })" (click)="sellerMessageDrawerOpen.set(false)">
                        @if (message.listingImageUrl) {
                          <img [src]="normalizeAssetUrl(message.listingImageUrl)" [alt]="message.listingTitle" loading="lazy" />
                        }
                        <span>{{ message.listingTitle }}</span>
                      </a>
                    }
                    @if (message.attachments?.length) {
                      <div class="seller-message-attachments">
                        @for (attachment of message.attachments; track attachment.threadFileID) {
                          @if (attachmentIsImage(attachment)) {
                            <button type="button" class="seller-message-media" (click)="downloadThreadFile(attachment)">
                              @if (threadFilePreviewUrl(attachment.threadFileID)) {
                                <img [src]="threadFilePreviewUrl(attachment.threadFileID)!" alt="Mesaj görseli" loading="lazy" />
                              } @else {
                                <span>{{ attachment.fileName || 'Görseli indir' }}</span>
                              }
                            </button>
                          } @else {
                            <button type="button" class="seller-message-file" (click)="downloadThreadFile(attachment)">
                              {{ attachment.fileName || 'Dosyayı indir' }}
                            </button>
                          }
                        }
                      </div>
                    } @else if (message.mediaUrl) {
                      <div class="seller-message-media">
                        <img [src]="normalizeAssetUrl(message.mediaUrl)" alt="Mesaj görseli" loading="lazy" />
                      </div>
                    } @else if (message.mediaID) {
                      <small>Dosya eklendi</small>
                    }
                    <p>{{ message.text }}</p>
                  </div>
                } @empty {
                  <small>Bu konuşmada henüz mesaj yok.</small>
                }
              </div>
              <div class="seller-chat-composer">
                @if (sellerMessageUploads().length) {
                  <div class="seller-media-preview seller-media-preview--file">
                    <div class="seller-media-preview__list">
                      @for (upload of sellerMessageUploads(); track upload.uploadID) {
                        <div class="seller-media-preview__item">
                          @if (upload.isImage && upload.previewUrl) {
                            <img [src]="upload.previewUrl" [alt]="upload.fileName" loading="lazy" />
                          } @else {
                            <strong>{{ uploadExtension(upload.fileName) || 'DOSYA' }}</strong>
                          }
                          <span>{{ upload.fileName }}</span>
                        </div>
                      }
                    </div>
                    <button type="button" (click)="clearSellerMessageMedia()">Temizle</button>
                  </div>
                }
                <div class="seller-chat-input-row">
                  <label class="seller-chat-plus" title="Dosya ekle">
                    +
                    <input type="file" multiple [disabled]="sellerMessageUploading() || sellerMessageUploads().length >= 6" (change)="uploadSellerMessageImage($event)" />
                  </label>
                  <input [(ngModel)]="sellerMessage" name="sellerMessage" placeholder="Mesaj yaz..." (keydown.enter)="$event.preventDefault(); sendSellerMessage(item)" />
                  <button type="button" class="seller-chat-send" [disabled]="messageSending() || (!sellerMessage.trim() && !sellerMessageUploads().length)" (click)="sendSellerMessage(item)">Gönder</button>
                </div>
              </div>
            }
          </aside>
        }
      } @else {
        <section class="page-card">
          <p class="eyebrow">{{ commerce() ? copy().product : copy().reading }}</p>
          <h1>{{ loadError() || copy().notFound }}</h1>
          <p class="field-help">{{ copy().retryConnection }}</p>
          <a class="back-link" [routerLink]="i18n.routePath('fortune')">{{ commerce() ? copy().backProducts : i18n.t('backToFortuneSelection') }}</a>
        </section>
      }
    </section>
  `,
  styleUrl: './fortune-widget.component.scss'
})
export class FortuneDetailWidgetComponent {
  private readonly route = inject(ActivatedRoute);
  private readonly fortune = inject(FortuneService);
  private readonly api = inject(ApiClientService);
  private readonly formUploader = inject(FormUploadService);
  protected readonly site = inject(SiteConfigService);
  private readonly cart = inject(CommerceCartService);
  private readonly router = inject(Router);
  protected readonly auth = inject(AuthService);
  protected readonly i18n = inject(I18nService);
  private readonly seo = inject(SeoService);
  private readonly pageData = inject(PageDataService);
  private readonly analytics = inject(AnalyticsService);
  private sharedOptionValues: Record<number, number> | null = null;
  protected readonly detail = signal<FortuneListingDetail | null>(null);
  protected readonly loading = signal(true);
  protected readonly loadError = signal('');
  protected readonly selectedOptions = signal<Record<number, number>>({});
  protected readonly customValues = signal<Record<string, unknown>>({});
  protected readonly uploadingFields = signal<Record<string, boolean>>({});
  protected readonly ordering = signal(false);
  protected readonly activeImageIndex = signal(0);
  private purchaseQuantity = 1;
  protected readonly zoomOpen = signal(false);
  protected readonly zoomScale = signal(1);
  protected readonly zoomOffsetX = signal(0);
  protected readonly zoomOffsetY = signal(0);
  protected readonly descriptionExpanded = signal(false);
  protected readonly orderError = signal('');
  protected readonly orderSuccess = signal('');
  protected readonly fortuneConfirmOpen = signal(false);
  protected readonly pendingFortune = signal<FortuneListingDetail | null>(null);
  protected readonly creditTopUpOpen = signal(false);
  protected readonly creditTopUpUrl = signal('');
  protected readonly creditTopUpFrameReady = signal(false);
  protected readonly completedOrderID = signal(0);
  protected readonly successCountdown = signal(0);
  protected readonly loginPromptOpen = signal(false);
  protected readonly messageSending = signal(false);
  protected readonly messageNotice = signal('');
  protected priceUnit(currency: string | null | undefined): string { const lang=this.i18n.lang(); return String(currency||'').toUpperCase()==='CREDIT' ? ({tr:'Kredi',en:'credits',de:'Guthaben',es:'créditos',fr:'crédits'} as Record<string,string>)[lang] || 'credits' : String(currency||'TRY'); }
  private loadSequence = 0;
  private initialSeedConsumed = false;
  protected readonly sellerConversation = signal<Array<{
    text: string;
    mine: boolean;
    mediaID?: number | null;
    threadFileID?: number | null;
    mediaUrl?: string | null;
    fileUrl?: string | null;
    fileName?: string | null;
    mimeType?: string | null;
    isImage?: boolean;
    attachments?: Array<{
      threadFileID: number;
      fileName?: string | null;
      mimeType?: string | null;
      extension?: string | null;
      isImage?: number;
    }>;
    listingSlug?: string | null;
    listingTitle?: string | null;
    listingImageUrl?: string | null;
  }>>([]);
  protected readonly sellerThreadList = signal<Array<{
    id: number;
    sellerID?: number;
    sellerName?: string;
    sellerAvatar?: string | null;
    sellerAvatarSizes?: Record<string, unknown> | unknown[] | null;
    lastMessage?: string | null;
    lastMessageAt?: string | null;
    hasUnread?: boolean;
    threadStatusLabel?: string | null;
  }>>([]);
  protected readonly sellerConversationOpen = signal(false);
  protected readonly activeSellerName = signal('');
  protected readonly activeSellerThreadID = signal(0);
  protected readonly sellerMessageContextListingID = signal<number | null>(null);
  protected readonly sellerMessageContextListingVersionID = signal<number | null>(null);
  protected readonly sellerMessageUploading = signal(false);
  protected readonly sellerMessageUploads = signal<Array<{
    uploadID: number;
    fileName: string;
    isImage: boolean;
    previewUrl: string | null;
  }>>([]);
  protected readonly threadFilePreviewUrls = signal<Record<number, string>>({});
  protected readonly sellerMessageDrawerOpen = signal(false);
  protected readonly sameCategoryListings = signal<FortuneListing[]>([]);
  protected readonly sameSellerListings = signal<FortuneListing[]>([]);
  protected readonly reviewTab = signal<'listing' | 'seller'>('listing');
  protected readonly reviewPage = signal(1);
  protected readonly reviewPageSize = signal(6);
  protected readonly sellerMessagingEnabled = computed(() => !!this.site.config().features?.sellerMessaging);
  private readonly themeProductSettings = computed<Record<string, unknown>>(() => {
    const presentation = this.pageData.get<{page?:{sections?:Array<{type?:string;settings?:Record<string,unknown>}>}}>('themePresentation');
    return presentation?.page?.sections?.find((section) => section.type === 'product-main')?.settings ?? {};
  });
  protected readonly showThemeReaderImage = computed(() => this.themeBoolean('show_reader_image', true));
  protected readonly showThemeReaderName = computed(() => this.themeBoolean('show_reader_name', true));
  protected readonly showThemeReaderRating = computed(() => this.themeBoolean('show_reader_rating', true));
  protected readonly emphasizeThemeCredit = computed(() => this.themeBoolean('emphasize_credit', this.site.config().paymentMode === 'credit'));
  protected readonly showThemeBreadcrumb = computed(() => this.themeBoolean('show_breadcrumb', true));
  protected readonly showThemeTitle = computed(() => this.themeBoolean('show_title', true));
  protected readonly showThemeSeller = computed(() => this.themeBoolean('show_seller', true));
  protected readonly showThemePrice = computed(() => this.themeBoolean('show_price', true));
  protected readonly showThemeFavorite = computed(() => this.themeBoolean('show_favorite', true));
  protected sellerMessage = '';
  protected readonly commerce = computed(() => this.site.enabled('commerce') && !this.site.experience('fortune'));
  protected detailMediaVisible(item:FortuneListingDetail):boolean{return (this.commerce()||this.showThemeReaderImage())&&Boolean(this.themeDetailImage(item))}
  private themeBoolean(key:string,fallback:boolean):boolean{const value=this.themeProductSettings()[key];return typeof value==='boolean'?value:fallback}
  protected themeString(key:string,fallback:string):string{const value=this.themeProductSettings()[key];return typeof value==='string'&&value.trim()?value:fallback}
  protected themeDetailImage(item:FortuneListingDetail):string|null{
    const source=this.themeString('media_source','auto');
    const listing=this.heroImage()?.url||item.imageUrl||null;
    if(source==='seller')return item.sellerImage||listing;
    if(source==='listing')return listing||item.sellerImage;
    return this.commerce()?listing:(item.sellerImage||listing);
  }
  protected themeDetailImageSizes(item:FortuneListingDetail):any{return this.themeString('media_source','auto')==='seller'?item.sellerImageSizes:this.heroImage()?.sizes}
  protected galleryThumbDisplaySizes():string{return this.themeString('desktop_gallery_layout','standard')==='magazine'?'(max-width: 900px) 72px, 38vw':'72px'}
  protected galleryImageAlt(title:string,index:number):string{return this.i18n.lang()==='tr'?`${title} görsel ${index}`:this.i18n.lang()==='de'?`${title} Bild ${index}`:this.i18n.lang()==='es'?`${title} imagen ${index}`:this.i18n.lang()==='fr'?`${title} image ${index}`:`${title} image ${index}`}
  protected sizeDecisionNote(option:{name?:string|null;title?:string|null}):string{if(!/size|dimension|ölçü|beden/i.test(String(option.name||option.title||'')))return'';return this.i18n.lang()==='tr'?'Beden adları üreticiden geldiği biçimde gösterilir. Siparişten önce yatak ölçüsünü ve dış çerçeve ölçülerini teyit edin.':this.i18n.lang()==='de'?'Größenbezeichnungen werden wie vom Hersteller geliefert angezeigt. Bestätigen Sie Matratzen- und Außenmaße vor der Bestellung.':this.i18n.lang()==='es'?'Los nombres de tamaño se muestran tal como los proporciona el fabricante. Confirma las medidas del colchón y del marco antes de comprar.':this.i18n.lang()==='fr'?'Les tailles sont affichées telles que fournies par le fabricant. Confirmez les dimensions du matelas et du cadre avant l’achat.':'Size names are shown exactly as supplied by the maker. Confirm mattress and outer-frame measurements before ordering.'}
  protected sizeHelpLabel():string{return this.i18n.lang()==='tr'?'Ölçüleri sorun':this.i18n.lang()==='de'?'Maße anfragen':this.i18n.lang()==='es'?'Consultar medidas':this.i18n.lang()==='fr'?'Demander les dimensions':'Ask about dimensions'}
  protected themeSellerLabel(item:FortuneListing):string{
    const identity=this.themeString('seller_identity',this.commerce()?'store':'full_name');
    if(identity==='full_name')return item.sellerName||item.sellerShopName||'';
    if(identity==='handle')return item.sellerSlug||item.sellerShopName||item.sellerName||'';
    return item.sellerShopName||item.sellerName||'';
  }
  protected favoritesEnabled(): boolean { return this.site.enabled('favorites'); }
  protected readonly copy = computed(() => ({
    tr: {
      product: 'Ürün', reading: 'Fal', products: 'Ürünler', productLoading: 'Ürün yükleniyor.', readingLoading: 'Fal yükleniyor.',
      category: 'Kategori', backProducts: 'Ürünlere dön', rating: 'puan', reviews: 'yorum', options: 'Seçenekler', option: 'Seçenek',
      subOption: 'Alt seçenek', customizations: 'Ürün özelleştirmeleri', readingDetails: 'Fal bilgileri', select: 'Seçiniz',
      uploadVideo: 'Video yükle', uploadImage: 'Görsel yükle', files: 'dosya', uploading: 'Yükleniyor...', remove: 'Kaldır',
      summary: 'Özet', faq: 'Sık sorulan sorular', shipping: 'Kargo', shippingAvailable: 'Kargo seçenekleri ödeme adımında gösterilir.', daysTotal: 'gün toplam', returnDays: 'gün içinde iade hakkı', downloadLimit: 'İndirme limiti', cancellationAllowed: 'İptal edilebilir', cancellationDenied: 'İptal edilemez', returnShippingCustomer: 'İade kargo ücretini alıcı öder.', returnShippingSeller: 'İade kargo ücretini satıcı öder.', policies: 'Politikalar', noCancellation: 'Bu ürün için sipariş iptali veya iade mümkün değildir.', safePurchase: ' mağazasından güvenle satın al.', willInterpret: ' falını yorumlayacak.', addingCart: 'Sepete ekleniyor...',
      shippingUnavailable: 'Seçtiğiniz ülkeye gönderilemiyor.', changeCountry: 'Ülkeyi değiştir', shippingFee: 'Nakliye ücreti:', deliveryEstimate: 'Tahmini teslim tarihi:', production: 'Üretim ve hazırlık:', shippingTime: 'Kargo süresi:', weeks: 'hafta', days: 'gün', productionOnRequest: 'Süre üreticiye göre değişir.', shippingOnRequest: 'Süre bilgisi mevcut değil.', addCart: 'Sepete ekle', quickBuy: 'Hızlı al', checkoutNote: 'Seçtiğin paketle doğrudan güvenli ödeme adımına geçilir.',
      preparingReading: 'Fal hazırlanıyor...', sendReading: 'Falını gönder', readingCheckoutNote: 'Ödeme kredi bakiyenden alınır.',
      askSeller: 'Satıcıya sor', openMessages: 'Mesaj panelini aç', loginToMessage: 'Giriş yapıp mesaj gönder', store: 'Mağaza', reader: 'Falcı', readingReviews: 'Bu fala yapılan yorumlar', readerReviews: 'Falcıya yapılan yorumlar',
      visitStore: 'Mağazaya git', readerPage: 'Falcı sayfası', sendMessage: 'Mesaj gönder', similarProducts: 'Benzer ürünler',
      similarReadings: 'Benzer fallar', sameCategory: 'Aynı kategorideki diğer seçenekler', sameSeller: 'Aynı falcıdan',
      otherOffers: 'diğer seçenekler', showMore: 'Devamını gör', showLess: 'Daha az göster', notFound: this.site.experience('fortune') ? 'Fal bulunamadı.' : 'Ürün bulunamadı.', retryConnection: 'Bağlantıyı kontrol edip tekrar deneyebilirsin.'
    },
    en: {
      product: 'Product', reading: 'Reading', products: 'Products', productLoading: 'Loading product.', readingLoading: 'Loading reading.',
      category: 'Category', backProducts: 'Back to products', rating: 'rating', reviews: 'reviews', options: 'Options', option: 'Option',
      subOption: 'Choice', customizations: 'Product customizations', readingDetails: 'Reading details', select: 'Select',
      uploadVideo: 'Upload video', uploadImage: 'Upload image', files: 'files', uploading: 'Uploading...', remove: 'Remove',
      summary: 'Summary', faq: 'Frequently asked questions', shipping: 'Shipping', shippingAvailable: 'Shipping options will be shown at checkout.', daysTotal: 'days total', returnDays: 'day return window', downloadLimit: 'Download limit', cancellationAllowed: 'Cancellation is allowed', cancellationDenied: 'Cancellation is not allowed', returnShippingCustomer: 'The customer pays return shipping.', returnShippingSeller: 'The seller pays return shipping.', policies: 'Policies', noCancellation: 'This product cannot be cancelled or returned.', safePurchase: ' shop offers secure checkout.', willInterpret: ' will interpret your reading.', addingCart: 'Adding to cart...',
      shippingUnavailable: 'This product cannot be shipped to your selected country.', changeCountry: 'Change country', shippingFee: 'Shipping fee:', deliveryEstimate: 'Estimated delivery date:', production: 'Production and preparation:', shippingTime: 'Shipping time:', weeks: 'weeks', days: 'days', productionOnRequest: 'Timing varies by manufacturer.', shippingOnRequest: 'Timing unavailable.', addCart: 'Add to cart', quickBuy: 'Buy now', checkoutNote: 'Continue directly to secure payment with your selected package.',
      preparingReading: 'Preparing reading...', sendReading: 'Send reading / checkout', readingCheckoutNote: 'Secure checkout starts with your selected options.',
      askSeller: 'Ask the seller', openMessages: 'Open messages', loginToMessage: 'Sign in to message', store: 'Store', reader: 'Reader', readingReviews: 'Reviews for this reading', readerReviews: 'Reviews for this reader',
      visitStore: 'Visit store', readerPage: 'Reader profile', sendMessage: 'Send message', similarProducts: 'Similar products',
      similarReadings: 'Similar readings', sameCategory: 'More choices in this category', sameSeller: 'From the same reader',
      otherOffers: 'other offers', showMore: 'Show more', showLess: 'Show less', notFound: this.site.experience('fortune') ? 'Reading not found.' : 'Product not found.', retryConnection: 'Check your connection and try again.'
    },
    de: {
      product: 'Produkt', reading: 'Deutung', products: 'Produkte', productLoading: 'Produkt wird geladen.', readingLoading: 'Deutung wird geladen.',
      category: 'Kategorie', backProducts: 'Zurück zu den Produkten', rating: 'Bewertung', reviews: 'Bewertungen', options: 'Optionen', option: 'Option',
      subOption: 'Auswahl', customizations: 'Produktanpassungen', readingDetails: 'Angaben zur Deutung', select: 'Bitte wählen',
      uploadVideo: 'Video hochladen', uploadImage: 'Bild hochladen', files: 'Dateien', uploading: 'Wird hochgeladen...', remove: 'Entfernen',
      summary: 'Zusammenfassung', shipping: 'Versand', shippingAvailable: 'Versandoptionen werden beim Checkout angezeigt.', daysTotal: 'Tage insgesamt', returnDays: 'Tage Rückgaberecht', downloadLimit: 'Download-Limit', cancellationAllowed: 'Stornierung möglich', cancellationDenied: 'Stornierung nicht möglich', returnShippingCustomer: 'Der Kunde trägt die Rücksendekosten.', returnShippingSeller: 'Der Verkäufer trägt die Rücksendekosten.', policies: 'Richtlinien', noCancellation: 'Dieses Produkt kann nicht storniert oder zurückgegeben werden.', safePurchase: ' bietet einen sicheren Einkauf.', willInterpret: ' wird deine Deutung übernehmen.', addingCart: 'Wird hinzugefügt...',
      faq: 'Häufige Fragen', shippingUnavailable: 'Dieses Produkt kann nicht in das ausgewählte Land versendet werden.', shippingFee: 'Versandkosten:', deliveryEstimate: 'Voraussichtliches Lieferdatum:', production: 'Produktion und Vorbereitung:', shippingTime: 'Versandzeit:', weeks: 'Wochen', days: 'Tage', productionOnRequest: 'Die Zeit variiert je nach Hersteller.', shippingOnRequest: 'Keine Zeitangabe verfügbar.', addCart: 'In den Warenkorb', quickBuy: 'Jetzt kaufen', checkoutNote: 'Mit deinem gewählten Paket geht es direkt zur sicheren Zahlung.',
      preparingReading: 'Deutung wird vorbereitet...', sendReading: 'Deutung senden / bezahlen', readingCheckoutNote: 'Die sichere Zahlung startet mit deiner Auswahl.',
      askSeller: 'Verkäufer fragen', openMessages: 'Nachrichten öffnen', loginToMessage: 'Anmelden und schreiben', store: 'Shop', reader: 'Berater', readingReviews: 'Bewertungen dieser Deutung', readerReviews: 'Bewertungen dieses Beraters',
      visitStore: 'Shop besuchen', readerPage: 'Beraterprofil', sendMessage: 'Nachricht senden', similarProducts: 'Ähnliche Produkte',
      similarReadings: 'Ähnliche Deutungen', sameCategory: 'Weitere Angebote in dieser Kategorie', sameSeller: 'Vom selben Berater',
      otherOffers: 'weitere Angebote', showMore: 'Mehr anzeigen', showLess: 'Weniger anzeigen', notFound: this.site.experience('fortune') ? 'Deutung nicht gefunden.' : 'Produkt nicht gefunden.', retryConnection: 'Prüfe die Verbindung und versuche es erneut.'
    },
    es: {
      product: 'Producto', reading: 'Lectura', products: 'Productos', productLoading: 'Cargando producto.', readingLoading: 'Cargando lectura.',
      category: 'Categoría', backProducts: 'Volver a productos', rating: 'puntuación', reviews: 'comentarios', options: 'Opciones', option: 'Opción',
      subOption: 'Selección', customizations: 'Personalizaciones', readingDetails: 'Datos de la lectura', select: 'Seleccionar',
      uploadVideo: 'Subir vídeo', uploadImage: 'Subir imagen', files: 'archivos', uploading: 'Subiendo...', remove: 'Eliminar',
      summary: 'Resumen', shipping: 'Envío', shippingAvailable: 'Las opciones de envío se mostrarán al pagar.', daysTotal: 'días en total', returnDays: 'días de devolución', downloadLimit: 'Límite de descargas', cancellationAllowed: 'Cancelación permitida', cancellationDenied: 'Cancelación no permitida', returnShippingCustomer: 'El cliente paga el envío de devolución.', returnShippingSeller: 'El vendedor paga el envío de devolución.', policies: 'Políticas', noCancellation: 'Este producto no se puede cancelar ni devolver.', safePurchase: ' ofrece una compra segura.', willInterpret: ' interpretará tu lectura.', addingCart: 'Añadiendo...',
      faq: 'Preguntas frecuentes', shippingUnavailable: 'Este producto no se puede enviar a tu país seleccionado.', changeCountry: 'Cambiar país', shippingFee: 'Coste de envío:', deliveryEstimate: 'Fecha de entrega estimada:', production: 'Producción y preparación:', shippingTime: 'Tiempo de envío:', weeks: 'semanas', days: 'días', productionOnRequest: 'El plazo depende del fabricante.', shippingOnRequest: 'Plazo no disponible.', addCart: 'Añadir al carrito', quickBuy: 'Comprar ahora', checkoutNote: 'Continúa directamente al pago seguro con el paquete seleccionado.',
      preparingReading: 'Preparando lectura...', sendReading: 'Enviar lectura / pagar', readingCheckoutNote: 'El pago seguro comienza con tus opciones.',
      askSeller: 'Preguntar al vendedor', openMessages: 'Abrir mensajes', loginToMessage: 'Inicia sesión para escribir', store: 'Tienda', reader: 'Vidente', readingReviews: 'Reseñas de esta lectura', readerReviews: 'Reseñas del vidente',
      visitStore: 'Visitar tienda', readerPage: 'Perfil del vidente', sendMessage: 'Enviar mensaje', similarProducts: 'Productos similares',
      similarReadings: 'Lecturas similares', sameCategory: 'Más opciones de esta categoría', sameSeller: 'Del mismo vidente',
      otherOffers: 'otras opciones', showMore: 'Ver más', showLess: 'Ver menos', notFound: this.site.experience('fortune') ? 'Lectura no encontrada.' : 'Producto no encontrado.', retryConnection: 'Comprueba la conexión e inténtalo de nuevo.'
    },
    fr: {
      product: 'Produit', reading: 'Voyance', products: 'Produits', productLoading: 'Chargement du produit.', readingLoading: 'Chargement de la voyance.',
      category: 'Catégorie', backProducts: 'Retour aux produits', rating: 'note', reviews: 'avis', options: 'Options', option: 'Option',
      subOption: 'Choix', customizations: 'Personnalisations', readingDetails: 'Informations de voyance', select: 'Sélectionner',
      uploadVideo: 'Ajouter une vidéo', uploadImage: 'Ajouter une image', files: 'fichiers', uploading: 'Téléchargement...', remove: 'Retirer',
      summary: 'Récapitulatif', shipping: 'Livraison', shippingAvailable: 'Les options de livraison seront affichées au paiement.', daysTotal: 'jours au total', returnDays: 'jours pour retourner', downloadLimit: 'Limite de téléchargement', cancellationAllowed: 'Annulation autorisée', cancellationDenied: 'Annulation non autorisée', returnShippingCustomer: 'Le client paie les frais de retour.', returnShippingSeller: 'Le vendeur paie les frais de retour.', policies: 'Politiques', noCancellation: 'Ce produit ne peut pas être annulé ou retourné.', safePurchase: ' propose un achat sécurisé.', willInterpret: ' interprétera votre voyance.', addingCart: 'Ajout en cours...',
      faq: 'Questions fréquentes', shippingUnavailable: 'Ce produit ne peut pas être livré dans votre pays sélectionné.', changeCountry: 'Changer de pays', shippingFee: 'Frais de livraison :', deliveryEstimate: 'Date de livraison estimée :', production: 'Production et préparation :', shippingTime: 'Délai de livraison :', weeks: 'semaines', days: 'jours', productionOnRequest: 'Le délai varie selon le fabricant.', shippingOnRequest: 'Délai indisponible.', addCart: 'Ajouter au panier', quickBuy: 'Acheter maintenant', checkoutNote: 'Passez directement au paiement sécurisé avec le forfait choisi.',
      preparingReading: 'Préparation...', sendReading: 'Envoyer / payer', readingCheckoutNote: 'Le paiement sécurisé démarre avec vos options.',
      askSeller: 'Contacter le vendeur', openMessages: 'Ouvrir les messages', loginToMessage: 'Connectez-vous pour écrire', store: 'Boutique', reader: 'Voyant', readingReviews: 'Avis sur cette voyance', readerReviews: 'Avis sur le voyant',
      visitStore: 'Voir la boutique', readerPage: 'Profil du voyant', sendMessage: 'Envoyer un message', similarProducts: 'Produits similaires',
      similarReadings: 'Voyances similaires', sameCategory: 'Autres choix de cette catégorie', sameSeller: 'Du même voyant',
      otherOffers: 'autres offres', showMore: 'Voir plus', showLess: 'Voir moins', notFound: this.site.experience('fortune') ? 'Voyance introuvable.' : 'Produit introuvable.', retryConnection: 'Vérifiez la connexion et réessayez.'
    }
  }[this.i18n.lang()]));
  protected readonly heroImage = computed(() => this.detail()?.images[this.activeImageIndex()] ?? this.detail()?.images[0] ?? null);
  protected readonly zoomImageUrl = computed(() => {
    const image = this.heroImage();
    const url = String(image?.url ?? '').trim();
    if (!url.includes('[size]')) {
      return this.normalizeAssetUrl(url);
    }
    const sizes = image?.sizes;
    const candidates = Array.isArray(sizes) ? sizes : sizes && typeof sizes === 'object' ? Object.values(sizes) : [];
    const tokens = candidates.flatMap((value) => typeof value === 'string' ? [value.toLowerCase()] : []);
    const preferred = tokens.includes('original') ? 'original' : tokens.includes('xl') ? 'xl' : tokens.includes('b') ? 'b' : 'm';
    return this.normalizeAssetUrl(url.replace('[size]', preferred));
  });
  protected readonly zoomTransform = computed(() => `translate3d(${this.zoomOffsetX()}px, ${this.zoomOffsetY()}px, 0) scale(${this.zoomScale()})`);
  protected readonly sellerLink = computed(() => {
    const item = this.detail();
    const slug = item?.sellerSlug || item?.sellerShopName || '';
    return slug ? this.i18n.routePath('seller', { slug }) : null;
  });
  protected readonly categoryLink = computed(() => {
    const item = this.detail();
    const slug = item?.categorySlug || '';
    return slug ? this.i18n.routePath('category', { slug }) : null;
  });
  protected readonly totalPrice = computed(() => {
    const item = this.detail();
    if (!item) {
      return 0;
    }
    const selected = this.selectedOptions();
    let variationPrice: number | null = null;
    const optionTotal = item.options.reduce((total, option, index) => {
      const selectedID = selected[option.id || index];
      const sub = option.subOptions.find((entry) => entry.id === selectedID);
      if (option.processType === 'variation' && sub?.price !== null && sub?.price !== undefined) {
        variationPrice = Number(sub.price);
        return total;
      }
      return total + (sub?.price ?? 0);
    }, 0);
    if (variationPrice !== null) {
      return variationPrice + optionTotal;
    }
    const basePrice = Number(item.price ?? 0);
    if (basePrice > 0) {
      return basePrice + optionTotal;
    }
    const variationPrices = item.options
      .filter((option) => option.processType === 'variation')
      .flatMap((option) => option.subOptions.map((sub) => Number(sub.price ?? 0)))
      .filter((price) => price > 0);
    return (variationPrices.length ? Math.min(...variationPrices) : 0) + optionTotal;
  });

  protected campaignPrice(): number | null {
    const item = this.detail();
    const campaign = item?.campaign;
    if (!campaign || campaign.discountValue === null || campaign.discountValue === undefined) {
      return null;
    }
    const price = this.totalPrice();
    return campaign.discountType === 'percent'
      ? Math.max(0, price * (1 - Number(campaign.discountValue) / 100))
      : Math.max(0, price - Number(campaign.discountValue));
  }

  protected fortuneCreditCost(): number {
    return Math.max(0, Number(this.campaignPrice() ?? this.totalPrice()));
  }

  protected readerPresenceLabel(item:FortuneListingDetail):string{
    const status=item.online?'online':String(item.presenceStatus??item.liveStatus??'offline').toLowerCase();
    const tr:Record<string,string>={online:'Çevrimiçi',on_duty:'Nöbetçi',reading:'Fal bakıyor',intraday:'Bugün müsait',offline:'Çevrimdışı'};
    const en:Record<string,string>={online:'Online',on_duty:'On duty',reading:'In a reading',intraday:'Available today',offline:'Offline'};
    return (this.i18n.lang()==='tr'?tr:en)[status]??(this.i18n.lang()==='tr'?'Çevrimdışı':'Offline');
  }

  protected creditPaymentMode(item: FortuneListingDetail): boolean {
    return String(item.currency ?? '').toUpperCase() === 'CREDIT';
  }

  protected sendButtonLabel(item: FortuneListingDetail): string {
    return this.creditPaymentMode(item)
      ? `${this.fortuneCreditCost().toLocaleString(this.i18n.lang())} Kredi - ${this.site.text('sendReading', this.copy().sendReading)}`
      : this.copy().sendReading;
  }

  protected commerceBuyLabel(): string {
    return ({ tr: 'Hemen al', en: 'Buy now', de: 'Jetzt kaufen', es: 'Comprar ahora', fr: 'Acheter maintenant' } as Record<string, string>)[this.i18n.lang()] || 'Buy now';
  }

  protected paymentGateways(item: FortuneListingDetail): FortunePaymentGateway[] {
    const allowed = this.auth.loggedIn() ? ['credit', 'wallet'] : ['creditcard'];
    const unique = new Map<string, FortunePaymentGateway>();
    for (const gateway of (item.paymentGateways ?? [])) {
      const method = String(gateway.method).toLowerCase();
      if (!allowed.includes(method)) continue;
      const current = unique.get(method);
      // A product can receive more than one gateway row for the same method
      // (for example a fast and a checkout credit row). The detail page is a
      // payment-method selector, so render one button per method and prefer
      // the fast-payment row when both exist.
      if (!current || (gateway.fastPayment && !current.fastPayment)) unique.set(method, gateway);
    }
    return Array.from(unique.values());
  }

  protected hasFastPayment(item: FortuneListingDetail): boolean {
    return this.paymentGateways(item).some((gateway) => gateway.fastPayment);
  }

  protected paymentGatewayLabel(item: FortuneListingDetail, gateway: FortunePaymentGateway): string {
    const action = this.site.text('sendReading', this.copy().sendReading);
    const method = String(gateway.method).toLowerCase();
    if (method === 'credit') {
      return `${this.fortuneCreditCost().toLocaleString(this.i18n.lang())} Kredi - ${action}`;
    }
    if (method === 'wallet') {
      return `Cüzdan - ${action}`;
    }
    if (method === 'creditcard') {
      return `${this.fortuneCreditCost().toLocaleString(this.i18n.lang())} Kredi - Misafir olarak devam et`;
    }
    return `${method} - ${action}`;
  }

  protected creditNeedsTopUp(item: FortuneListingDetail, gateway: FortunePaymentGateway): boolean {
    return String(gateway.method).toLowerCase() === 'credit'
      && this.auth.loggedIn()
      && this.auth.creditBalance() !== null
      && Number(this.auth.creditBalance()) < this.fortuneCreditCost();
  }

  protected goToCreditTopUp(): void {
    const returnPath=this.saveCreditReturnContext();
    const query=new URLSearchParams({fortuneReturn:returnPath});
    void this.router.navigateByUrl(`${this.i18n.routePath('creditTopUp')}?${query.toString()}`);
  }

  protected discountedOptionPrice(value: number | null | undefined): number {
    const price=Number(value ?? 0);
    const campaign=this.detail()?.campaign;
    if(!campaign || campaign.discountValue===null || campaign.discountValue===undefined) return price;
    return campaign.discountType==='percent'
      ? Math.max(0,price*(1-Number(campaign.discountValue)/100))
      : Math.max(0,price-Number(campaign.discountValue));
  }

  protected hasOptionDiscount(value: number | null | undefined): boolean {
    const original = Number(value ?? 0);
    return original > 0 && this.discountedOptionPrice(original) < original;
  }

  protected isPhysicalProduct(item: FortuneListingDetail): boolean {
    const type = String(item.listingType ?? '').toLowerCase().trim();
    return !['digital', 'download', 'service', 'fortune', 'reading', 'subscription'].some((value) => type.includes(value));
  }

  private ensureSubscriptionAuth(item:FortuneListingDetail):boolean{
    if(String(item.listingType??'').toLowerCase()!=='subscription'||this.auth.loggedIn())return true;
    this.orderError.set('Abonelik ürününü satın almak için giriş yapmalısın.');
    this.loginPromptOpen.set(true);
    return false;
  }

  protected shippingOptions(item: FortuneListingDetail): Array<Record<string, unknown>> {
    const raw: unknown = item.shipping;
    if (Array.isArray(raw)) return raw.filter((entry): entry is Record<string, unknown> => !!entry && typeof entry === 'object');
    if (raw && typeof raw === 'object') {
      const record = raw as Record<string, unknown>;
      const options = record['options'] ?? record['methods'] ?? record['services'] ?? record['list'];
      if (Array.isArray(options)) return options.filter((entry): entry is Record<string, unknown> => !!entry && typeof entry === 'object');
      const values = Object.values(record).flatMap((entry) => Array.isArray(entry) ? entry : [entry]).filter((entry): entry is Record<string, unknown> => !!entry && typeof entry === 'object');
      return values.length ? values : (Object.keys(record).length ? [record] : []);
    }
    return [];
  }

  protected shippingLabel(shipping: Record<string, unknown>): string {
    const name = String(shipping['title'] ?? shipping['name'] ?? shipping['method'] ?? shipping['service'] ?? 'Shipping').trim();
    const rawPrice = shipping['onePrice'] ?? shipping['price'] ?? shipping['shippingPrice'] ?? shipping['amount'];
    const price = shipping['free'] === true || shipping['isFree'] === true || shipping['priceType'] === 'free' || Number(rawPrice ?? -1) === 0
      ? 'Free'
      : `${rawPrice ?? ''} ${this.detail()?.currency ?? ''}`.trim();
    const min = Number(shipping['minDay'] ?? shipping['customMinDay'] ?? shipping['minDays'] ?? 0);
    const max = Number(shipping['maxDay'] ?? shipping['customMaxDay'] ?? shipping['maxDays'] ?? min);
    const prep = Number(this.detail()?.preparationDays ?? 0);
    const total = min || max ? `${prep + min}–${prep + max} ${this.copy().daysTotal}` : '';
    return [name, price, total].filter(Boolean).join(' · ');
  }

  protected shippingName(shipping: Record<string, unknown>): string {
    return String(shipping['title'] ?? shipping['name'] ?? shipping['method'] ?? shipping['service'] ?? 'Shipping').trim();
  }

  protected shippingPrice(shipping: Record<string, unknown>): string {
    const rawPrice = shipping['onePrice'] ?? shipping['price'] ?? shipping['shippingPrice'] ?? shipping['amount'];
    if (shipping['free'] === true || shipping['isFree'] === true || shipping['priceType'] === 'free' || Number(rawPrice ?? -1) === 0) return 'Free';
    const price = Number(rawPrice);
    const currency = String(shipping['currency'] ?? this.detail()?.currency ?? '').trim();
    return `${Number.isFinite(price) ? new Intl.NumberFormat(this.i18n.lang() || 'en', { maximumFractionDigits: 2 }).format(price) : String(rawPrice ?? '')} ${currency}`.trim();
  }

  private shippingDays(shipping: Record<string, unknown>, side: 'min' | 'max'): number {
    const key = side === 'min' ? 'minShipping' : 'maxShipping';
    const value = Number(shipping[key] ?? 0);
    const period = String(shipping['shippingPeriod'] ?? '').toLowerCase();
    if (!Number.isFinite(value) || value <= 0) return 0;
    if (period === 'week' || period === 'weeks') return value * 7;
    if (period === 'month' || period === 'months') return value * 30;
    return value;
  }

  protected productionWindow(shipping: Record<string, unknown>): string {
    const min = this.shippingDays(shipping, 'min');
    const max = this.shippingDays(shipping, 'max');
    const period = String(shipping['shippingPeriod'] ?? '').toLowerCase();
    if (!min && !max) {
      const preparation = Number(this.detail()?.preparationDays ?? 0);
      return preparation > 0 ? `${preparation} ${this.copy().days ?? ''}`.trim() : (this.copy().productionOnRequest ?? '');
    }
    if (period.includes('week')) return `${Math.ceil(min / 7)}–${Math.ceil(max / 7)} ${this.copy().weeks}`;
    return `${min}–${max} ${this.copy().days}`;
  }

  protected transitWindow(shipping: Record<string, unknown>): string {
    const min = Number(shipping['minDay'] ?? shipping['customMinDay'] ?? shipping['minDays'] ?? 0);
    const max = Number(shipping['maxDay'] ?? shipping['customMaxDay'] ?? shipping['maxDays'] ?? min);
    return min || max ? `${min}–${max} ${this.copy().days ?? ''}`.trim() : (this.copy().shippingOnRequest ?? '');
  }

  protected deliveryWindow(shipping: Record<string, unknown>): string {
    const preparationMin = this.shippingDays(shipping, 'min') || Number(this.detail()?.preparationDays ?? 0);
    const preparationMax = this.shippingDays(shipping, 'max') || preparationMin;
    const shippingMin = Number(shipping['minDay'] ?? shipping['customMinDay'] ?? shipping['minDays'] ?? 0);
    const shippingMax = Number(shipping['maxDay'] ?? shipping['customMaxDay'] ?? shipping['maxDays'] ?? shippingMin);
    if (!(preparationMin || preparationMax || shippingMin || shippingMax)) return this.copy().shippingOnRequest ?? '';
    const now = new Date();
    const minDate = new Date(now);
    const maxDate = new Date(now);
    minDate.setDate(now.getDate() + preparationMin + shippingMin);
    maxDate.setDate(now.getDate() + preparationMax + shippingMax);
    const locale = this.i18n.lang() === 'tr' ? 'tr-TR' : this.i18n.lang();
    const format = new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short', year: 'numeric' });
    return `${format.format(minDate)} – ${format.format(maxDate)}`;
  }

  protected faqItems(item: FortuneListingDetail): Array<Record<string, unknown>> {
    const unwrap = (value: unknown): unknown => {
      if (Array.isArray(value)) return value;
      if (!value || typeof value !== 'object') return [];
      const record = value as Record<string, unknown>;
      return record['items'] ?? record['list'] ?? record['data'] ?? record['faqs'] ?? record['questions'] ?? [];
    };
    const value = unwrap(item.faqs);
    return Array.isArray(value)
      ? value.filter((entry): entry is Record<string, unknown> => !!entry && typeof entry === 'object')
      : [];
  }

  protected faqText(faq: Record<string, unknown>): string {
    return String(faq['content'] ?? faq['answer'] ?? faq['description'] ?? '');
  }

  protected openLocaleSelector(): void {
    if (typeof window !== 'undefined') window.dispatchEvent(new Event('aymudo:open-locale'));
  }

  protected policyEntries(item: FortuneListingDetail): Array<{ key: string; label: string; value: string }> {
    const source = item.policies && typeof item.policies === 'object' ? item.policies : {};
    const entries: Array<{ key: string; label: string; value: string }> = [];
    const visit = (record: Record<string, unknown>, prefix = '') => {
      for (const [key, value] of Object.entries(record)) {
        if (value === null || value === undefined || value === '' || ['id', 'versionID'].includes(key)) continue;
        if (value && typeof value === 'object' && !Array.isArray(value)) {
          visit(value as Record<string, unknown>, prefix ? `${prefix}.${key}` : key);
          continue;
        }
        const text = String(value).trim();
        if (!text) continue;
        const label = key.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/[_-]+/g, ' ').replace(/^./, (char) => char.toUpperCase());
        entries.push({ key: `${prefix}.${key}`, label, value: text });
      }
    };
    visit(source);
    return entries;
  }

  protected returnDays(item: FortuneListingDetail): number | null {
    const value = this.policyValue(item, ['returnDays', 'return_days', 'days']);
    const days = Number(value ?? 0);
    return this.isPhysicalProduct(item) && days > 0 ? days : null;
  }

  protected returnAccepted(item: FortuneListingDetail): number | null {
    const value = this.policyValue(item, ['returnsAccepted', 'returnAccepted', 'returns_accepted']);
    return value === true || value === 1 || ['1', 'true', 'yes'].includes(String(value).toLowerCase()) ? this.returnDays(item) : null;
  }

  protected downloadLimit(item: FortuneListingDetail): number | null {
    if (this.isPhysicalProduct(item)) return null;
    const value = Number(this.policyValue(item, ['downloadLimit', 'download_limit']) ?? 0);
    return value > 0 ? value : null;
  }

  protected cancellationAllowed(item: FortuneListingDetail): boolean | null {
    const value = this.policyValue(item, ['cancellationAllowed', 'cancellation', 'cancelAllowed', 'cancel']);
    if (value === undefined || value === null || value === '') return null;
    return value === true || value === 1 || ['1', 'true', 'yes', 'allowed', 'accepted'].includes(String(value).toLowerCase());
  }

  protected returnShippingCustomer(item: FortuneListingDetail): string | null {
    const value = String(this.policyValue(item, ['returnShippingFees', 'return_shipping_fees', 'returnShipping']) ?? '').toLowerCase();
    return value === 'customer' || value === 'seller' ? value : null;
  }

  protected additionalPolicyEntries(item: FortuneListingDetail): Array<{ key: string; label: string; value: string }> {
    return this.policyEntries(item).filter((entry) => !/return|returns|download|cancellation|cancel|shipping/i.test(`${entry.key} ${entry.label}`));
  }

  private policyValue(item: FortuneListingDetail, keys: string[]): unknown {
    const source = item.policies ?? {};
    const wanted = new Set(keys.map((key) => key.toLowerCase()));
    for (const entry of this.policyEntries(item)) {
      if (wanted.has(entry.key.split('.').at(-1)?.toLowerCase() ?? '') || wanted.has(entry.label.toLowerCase().replace(/\s+/g, ''))) return entry.value;
    }
    return undefined;
  }

  protected hasNoCancellationPolicy(item: FortuneListingDetail): boolean {
    return this.policyEntries(item).some((entry) => /cancel|iptal|storno|return|iade|refund/i.test(`${entry.label} ${entry.value}`) && /no|not|non|cannot|hayır|yok|edilmez|mümkün değil|mümkün değildir/i.test(entry.value));
  }
  protected readonly sellerMessageMediaExtension = computed(() => {
    const firstFile = this.sellerMessageUploads()[0];
    const name = String(firstFile?.fileName ?? '').trim();
    if (!name || !name.includes('.')) {
      return '';
    }
    return name.split('.').pop()?.toUpperCase() ?? '';
  });

  protected zoomIn(): void {
    this.zoomScale.update((value) => Math.min(4, Number((value + 0.25).toFixed(2))));
  }

  protected zoomOut(): void {
    this.zoomScale.update((value) => {
      const next = Math.max(1, Number((value - 0.25).toFixed(2)));
      if (next === 1) {
        this.zoomOffsetX.set(0);
        this.zoomOffsetY.set(0);
      }
      return next;
    });
  }

  private zoomDragging = false;
  private zoomPointerX = 0;
  private zoomPointerY = 0;

  protected openZoom(): void {
    this.zoomScale.set(1);
    this.zoomOffsetX.set(0);
    this.zoomOffsetY.set(0);
    this.zoomOpen.set(true);
  }

  protected closeZoom(): void {
    this.zoomDragging = false;
    this.zoomOpen.set(false);
  }

  protected zoomPointerDown(event: PointerEvent): void {
    this.zoomDragging = true;
    this.zoomPointerX = event.clientX;
    this.zoomPointerY = event.clientY;
    (event.currentTarget as HTMLElement | null)?.setPointerCapture?.(event.pointerId);
  }

  protected zoomPointerMove(event: PointerEvent): void {
    if (!this.zoomDragging || this.zoomScale() <= 1) {
      return;
    }
    const dx = event.clientX - this.zoomPointerX;
    const dy = event.clientY - this.zoomPointerY;
    this.zoomPointerX = event.clientX;
    this.zoomPointerY = event.clientY;
    const limit = Math.max(80, (this.zoomScale() - 1) * 360);
    this.zoomOffsetX.update((value) => Math.max(-limit, Math.min(limit, value + dx)));
    this.zoomOffsetY.update((value) => Math.max(-limit, Math.min(limit, value + dy)));
  }

  protected zoomPointerUp(event: PointerEvent): void {
    this.zoomDragging = false;
    (event.currentTarget as HTMLElement | null)?.releasePointerCapture?.(event.pointerId);
  }

  protected toggleDescription(): void {
    this.descriptionExpanded.update((value) => !value);
  }

  protected previewImageOnPointer(event: MouseEvent): void {
    const item = this.detail();
    if (!this.commerce() || !item || item.images.length < 2) {
      return;
    }
    const target = event.currentTarget as HTMLElement | null;
    if (!target) {
      return;
    }
    const bounds = target.getBoundingClientRect();
    if (bounds.width <= 0) {
      return;
    }
    const position = Math.max(0, Math.min(0.9999, (event.clientX - bounds.left) / bounds.width));
    this.activeImageIndex.set(Math.min(item.images.length - 1, Math.floor(position * item.images.length)));
  }

  protected uploadExtension(fileName: string | null | undefined): string {
    const name = String(fileName ?? '').trim();
    if (!name || !name.includes('.')) {
      return '';
    }
    return name.split('.').pop()?.toUpperCase() ?? '';
  }

  protected attachmentIsImage(attachment: {
    isImage?: number | boolean | null;
    mimeType?: string | null;
    extension?: string | null;
    fileName?: string | null;
  }): boolean {
    if (attachment.isImage === true || Number(attachment.isImage ?? 0) > 0) {
      return true;
    }
    const mimeType = String(attachment.mimeType ?? '').toLowerCase();
    if (mimeType.startsWith('image/')) {
      return true;
    }
    const extension = String(attachment.extension ?? this.uploadExtension(attachment.fileName)).toLowerCase();
    return ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg', 'avif'].includes(extension);
  }

  constructor() {
    void this.auth.refreshCreditBalance();
    const initialSlug = this.route.snapshot.paramMap.get('slug') ?? '';
    const candidateSeed = this.fortune.seededListingDetail();
    const seeded = candidateSeed && candidateSeed.slug === initialSlug ? candidateSeed : null;
    if (seeded) {
      this.detail.set(seeded);
      this.loading.set(false);
    }
    this.route.queryParamMap.subscribe((params) => {
      this.purchaseQuantity = Math.max(1, Math.min(100, Number(params.get('branches')) || 1));
      const encoded = params.get('options');
      this.sharedOptionValues = this.decodeSharedOptions(encoded);
      if (this.detail() && this.sharedOptionValues) {
        this.selectedOptions.set({ ...this.selectedOptions(), ...this.sharedOptionValues });
      }
    });
    this.route.paramMap.subscribe((params) => {
      const slug = params.get('slug') ?? '';
      if (slug) {
        const useInitialSeed = !this.initialSeedConsumed && this.detail()?.slug === slug;
        this.initialSeedConsumed = true;
        if (!useInitialSeed && this.detail()?.slug !== slug) {
          this.loading.set(true);
          this.detail.set(null);
          this.loadError.set('');
          this.selectedOptions.set({});
          this.customValues.set({});
          this.activeImageIndex.set(0);
          this.descriptionExpanded.set(false);
          this.sameCategoryListings.set([]);
          this.sameSellerListings.set([]);
        }
        // The SSR listing is already the active listing for the first route.
        // Do not immediately reload it on the client: that second request can
        // race the SSR state and turn a valid first paint into "not found".
        // Only a real route change gets a new client-side detail request.
        if (useInitialSeed) return;
        void this.load(slug, false);
      }
    });
  }

  protected selectOption(optionID: number, subID: number): void {
    const scrollY = typeof window === 'undefined' ? 0 : window.scrollY;
    const selected = {
      ...this.selectedOptions(),
      [optionID]: subID
    };
    this.selectedOptions.set(selected);
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { options: this.encodeSharedOptions(selected) },
      queryParamsHandling: 'merge',
      replaceUrl: true
    }).then(() => {
      if (typeof window === 'undefined') return;
      // Updating query parameters must not move the customer away from the
      // option group they are currently using.
      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => window.scrollTo({ top: scrollY, left: 0, behavior: 'auto' }));
      });
    });
  }

  protected sellerLabel(item: FortuneListing): string {
    return this.commerce() ? (item.sellerShopName || item.sellerName) : item.sellerName;
  }

  protected reviewItems(item: FortuneListingDetail): Array<Record<string, unknown>> {
    const all = this.commerce() || this.reviewTab() === 'listing' ? item.reviews : item.sellerReviews;
    return all.slice(0, this.reviewPage() * this.reviewPageSize());
  }

  protected loadMoreReviews(): void {
    this.reviewPage.update((page) => page + 1);
  }

  private localizedDescription(item: FortuneListingDetail): string {
    const seller = this.sellerLabel(item);
    const copies = {
      tr: this.commerce() ? `${seller} tarafından sunulan ${item.title}.` : `${seller} tarafından hazırlanan fal deneyimi.`,
      en: this.commerce() ? `${item.title}, offered by ${seller}.` : `A personal reading experience prepared by ${seller}.`,
      de: this.commerce() ? `${item.title}, angeboten von ${seller}.` : `Eine persönliche Deutung von ${seller}.`,
      es: this.commerce() ? `${item.title}, ofrecido por ${seller}.` : `Una lectura personal preparada por ${seller}.`,
      fr: this.commerce() ? `${item.title}, proposé par ${seller}.` : `Une voyance personnalisée proposée par ${seller}.`
    };
    return copies[this.i18n.lang()];
  }

  protected async addToCart(item: FortuneListingDetail, event: MouseEvent): Promise<void> {
    if(!this.ensureSubscriptionAuth(item))return;
    if (this.ordering()) {
      return;
    }
    this.ordering.set(true);
    this.orderError.set('');
    this.orderSuccess.set('');
    try {
      await firstValueFrom(this.api.post<Record<string, unknown>>('cartAdd', {
        listingID: item.id,
        quantity: this.purchaseQuantity,
        country: this.i18n.country(),
        options: this.selectedOptions(),
        customization: this.customValues()
      }));
      this.cart.remember({ listingID: item.id, title: item.title, imageUrl: this.heroImage()?.url ?? item.imageUrl, quantity: this.purchaseQuantity });
      this.analytics.track('cart.add', { entityType: 'listing', entityID: item.id, entityVersionID: item.versionID ?? undefined, entityTitle: item.title });
      this.cart.animateToCart((event.currentTarget as HTMLElement | null)?.closest('.checkout-card') ?? null);
    } catch (error) {
      this.analytics.track('cart.error', {
        entityType: 'listing',
        entityID: item.id,
        entityVersionID: item.versionID ?? undefined,
        entityTitle: item.title,
        metadata: { message: this.apiErrorMessage(error) || 'cart_add_failed' }
      });
      this.orderError.set('Ürün sepete eklenemedi. Zorunlu seçenekleri ve stok durumunu kontrol edip tekrar dene.');
    } finally {
      this.ordering.set(false);
    }
  }

  protected customValue(field: { formKey: string }): unknown {
    return this.customValues()[field.formKey] ?? '';
  }

  protected setCustomValue(field: { formKey: string }, value: unknown): void {
    this.customValues.set({
      ...this.customValues(),
      [field.formKey]: value
    });
  }

  protected checkboxValue(field: { formKey: string }, value: string): boolean {
    const current = this.customValues()[field.formKey];
    return !!(current && typeof current === 'object' && !Array.isArray(current) && (current as Record<string, unknown>)[value]);
  }

  protected toggleCheckboxValue(field: { formKey: string }, value: string, event: Event): void {
    const checked = !!(event.target as HTMLInputElement | null)?.checked;
    const current = this.customValues()[field.formKey];
    const next = current && typeof current === 'object' && !Array.isArray(current) ? { ...(current as Record<string, boolean>) } : {};
    next[value] = checked;
    this.setCustomValue(field, next);
  }

  protected isUploadField(type: string): boolean {
    return ['image', 'picture', 'file', 'media', 'gallery', 'video'].includes(type);
  }

  protected uploadAccept(type: string): string {
    if (type === 'video') {
      return 'video/*';
    }
    if (type === 'file') {
      return 'image/*,application/pdf';
    }
    return 'image/*';
  }

  protected isWideField(type: string): boolean {
    return type === 'textarea' || this.isUploadField(type);
  }

  protected minFiles(field: { minFiles?: number | string | null; minLength?: number | string | null; minlenght?: number | string | null; minlength?: number | string | null; type: string }): number {
    if (!this.isUploadField(field.type)) return 0;
    const configured = Number(field.minFiles ?? field.minLength ?? field.minlenght ?? field.minlength ?? 0);
    return configured > 0 ? Math.min(configured, 12) : 0;
  }

  protected maxFiles(field: { maxFiles?: number | string | null; maxLength?: number | string | null; maxlenght?: number | string | null; maxlength?: number | string | null; type: string }): number {
    const configured = Number(field.maxFiles ?? field.maxLength ?? field.maxlenght ?? field.maxlength ?? 0);
    const max = configured > 0 ? configured : (field.type === 'gallery' ? 4 : 1);
    return Math.min(Math.max(max, 1), 12);
  }

  protected uploadedFiles(field: { formKey: string }): Record<string, unknown>[] {
    const value = this.customValues()[field.formKey];
    return Array.isArray(value) ? value.filter((item) => item && typeof item === 'object' && !(item as Record<string, unknown>)['isDeleted']) as Record<string, unknown>[] : [];
  }

  protected setUploadingField(fieldKey: string, state: boolean): void {
    this.uploadingFields.set({ ...this.uploadingFields(), [fieldKey]: state });
  }

  protected uploadedFileUrl(file: Record<string, unknown>): string | null {
    const url = String(file['url'] ?? file['tempFile'] ?? file['src'] ?? '');
    if (!url) {
      return null;
    }
    const prepared = url.includes('[size]') ? url.replace('[size]', 's') : url;
    return /^https?:\/\//.test(prepared) ? prepared : `${this.site.config().apiBaseUrl}/${prepared.replace(/^\/+/, '')}`;
  }

  protected fileName(file: Record<string, unknown>): string {
    return String(file['fileName'] ?? file['name'] ?? file['uploadID'] ?? 'Görsel');
  }

  protected fileKey(file: Record<string, unknown>): string {
    return String(file['uploadID'] ?? file['id'] ?? file['fileName'] ?? file['tempFile'] ?? file['url'] ?? this.fileName(file));
  }

  protected removeUploadedFile(field: { formKey: string }, file: Record<string, unknown>): void {
    const preview = String(file['tempFile'] ?? file['previewUrl'] ?? '');
    if (/^blob:/i.test(preview) && typeof URL !== 'undefined') {
      URL.revokeObjectURL(preview);
    }
    const files = this.uploadedFiles(field).filter((item) => item !== file);
    this.setCustomValue(field, files);
  }

  protected async uploadCustomizationFiles(field: { formKey: string; type: string; maxLength?: number | string | null; maxlenght?: number | string | null; maxlength?: number | string | null }, event: Event): Promise<void> {
    const input = event.target as HTMLInputElement | null;
    const files = Array.from(input?.files ?? []);
    if (!files.length) {
      return;
    }
    const allowed = files.slice(0, Math.max(this.maxFiles(field) - this.uploadedFiles(field).length, 0));
    if (!allowed.length) {
      if (input) {
        input.value = '';
      }
      return;
    }
    this.uploadingFields.set({ ...this.uploadingFields(), [field.formKey]: true });
    try {
      const uploaded: Record<string, unknown>[] = [];
      for (const file of allowed) {
        const normalized = await this.formUploader.upload(field.formKey, file, this.uploadUrl(field));
        uploaded.push(normalized as unknown as Record<string, unknown>);
      }
      this.setCustomValue(field, [...this.uploadedFiles(field), ...uploaded]);
    } catch {
      this.orderError.set('Görsel yüklenemedi. Dosyayı kontrol edip tekrar deneyelim.');
    } finally {
      this.uploadingFields.set({ ...this.uploadingFields(), [field.formKey]: false });
      if (input) {
        input.value = '';
      }
    }
  }

  protected async sendFortune(item: FortuneListingDetail): Promise<void> {
    if(!this.ensureSubscriptionAuth(item))return;
    const validationError=this.validatePurchase(item);
    if(validationError){if(this.openGuidedRequiredPurchaseFields(item,null))return;this.orderError.set(validationError);return}
    await this.executeFortune(item);
  }

  @HostListener('window:aymudo-guided-request-product-context')
  protected provideGuidedProductContext():void{
    const item=this.detail();
    if(!item||this.commerce())return;
    window.dispatchEvent(new CustomEvent('aymudo-guided-product-context',{detail:{
      label:'Bu falı göndermek istiyorum',
      start:async()=>{await this.sendFortune(item);}
    }}));
  }

  protected async payWithGateway(item: FortuneListingDetail, gateway: FortunePaymentGateway): Promise<void> {
    if(!this.ensureSubscriptionAuth(item))return;
    const validationError = this.validatePurchase(item);
    if (validationError) {
      if(this.openGuidedRequiredPurchaseFields(item,gateway))return;
      this.orderError.set(validationError);
      return;
    }
    await this.executeFortune(item, gateway);
  }

  protected cancelFortuneOrder():void{this.fortuneConfirmOpen.set(false);this.pendingFortune.set(null)}

  protected async confirmFortuneOrder():Promise<void>{
    const item=this.pendingFortune();
    if(!item)return;
    this.fortuneConfirmOpen.set(false);
    await this.executeFortune(item);
    this.pendingFortune.set(null);
  }

  protected openCreditTopUp(resumeCheckoutID = 0): void {
    const params=new URLSearchParams({fortuneReturn:this.saveCreditReturnContext()});
    if(resumeCheckoutID>0)params.set('resumeCheckoutID',String(resumeCheckoutID));
    this.creditTopUpFrameReady.set(false);
    this.creditTopUpUrl.set(`${this.i18n.routePath('creditTopUp')}?${params.toString()}`);
    this.creditTopUpOpen.set(true);
  }

  private saveCreditReturnContext():string{
    const returnPath=String(this.router.url||'/').split('#')[0];
    if(typeof window==='undefined')return returnPath;
    const serialized=JSON.stringify({returnPath,cost:this.fortuneCreditCost(),savedAt:Date.now()});
    try{window.sessionStorage.setItem('fortune.credit-return.v1',serialized);}catch{}
    try{window.localStorage.setItem('fortune.credit-return.v1',serialized);}catch{}
    return returnPath;
  }

  protected closeCreditTopUp(): void {
    this.creditTopUpOpen.set(false);
    this.creditTopUpUrl.set('');
    void this.auth.refreshCreditBalance();
  }

  private async executeFortune(item: FortuneListingDetail, selectedGateway: FortunePaymentGateway | null = null): Promise<void> {
    if (this.ordering()) {
      return;
    }
    this.ordering.set(true);
    this.orderError.set('');
    try {
      // Commerce uses the store's own active card gateway behind the single
      // "Buy now" action. This also lets a guest reach checkout so the
      // account/email step can be completed there instead of failing here.
      const gateway = selectedGateway ?? (this.commerce()
        ? (item.paymentGateways ?? []).find(candidate => String(candidate.method).toLowerCase() === 'creditcard') ?? null
        : null);
      if (gateway?.fastPayment && gateway.method === 'credit') {
        await this.auth.refreshCreditBalance();
        if (Number(this.auth.creditBalance() ?? 0) < this.fortuneCreditCost()) {
          this.openCreditTopUp();
          return;
        }
      }
      const payload = {
        listingID: item.id,
        quantity: this.purchaseQuantity,
        country: this.i18n.country(),
        options: this.selectedOptions(),
        customization: this.customValues(),
        buyNow: true,
        paymentGatewayID: gateway?.id ?? null,
        paymentMethod: gateway?.method ?? null,
        fastPayment: this.auth.loggedIn() && !!gateway
      };
      this.analytics.track('checkout.start', {
        entityType: 'listing',
        entityID: item.id,
        entityVersionID: item.versionID ?? undefined,
        entityTitle: item.title,
        metadata: { paymentMethod: gateway?.method ?? null, paymentGatewayID: gateway?.id ?? null, fastPayment: !!gateway }
      });
      const response = await firstValueFrom(this.api.post<Record<string, unknown>>('cartAdd', payload));
      const checkoutID = Number(this.findResponseValue(response, 'checkoutID') ?? 0);
      if (checkoutID > 0 && gateway?.fastPayment && gateway.method === 'credit') {
        this.openCreditTopUp(checkoutID);
        return;
      }
      if (checkoutID > 0) {
        window.dispatchEvent(new CustomEvent('aymudo-guided-purchase', { detail: {
          checkoutID,
          listingID: item.id,
          title: item.title,
          ordersUrl: this.i18n.routePath('orders')
        }}));
        return;
      }
      const orderID = Number(this.findResponseValue(response, 'orderID') ?? 0);
      if (orderID > 0) {
        this.orderSuccess.set('Falın başarıyla gönderildi.');
        this.completedOrderID.set(orderID);
        for(let second=3;second>0;second--){this.successCountdown.set(second);await new Promise(resolve=>setTimeout(resolve,1000))}
        if(this.completedOrderID()===orderID)await this.router.navigateByUrl(this.i18n.routePath('orders'));
        return;
      }
      throw new Error('fortune_checkout_incomplete');
    } catch (error) {
      this.analytics.track('checkout.error', {
        entityType: 'listing',
        entityID: item.id,
        entityVersionID: item.versionID ?? undefined,
        entityTitle: item.title,
        metadata: { message: this.apiErrorMessage(error) || 'checkout_failed' }
      });
      this.orderError.set(this.apiErrorMessage(error) || (this.commerce()
        ? 'Hızlı satın alma başlatılamadı. Ürün seçeneklerini kontrol et.'
        : 'Fal gönderme akışı başlatılamadı. Lütfen tekrar dene.'));
    } finally {
      this.ordering.set(false);
    }
  }

  private validatePurchase(item:FortuneListingDetail):string{
    for(const option of item.options||[]){if((option.required||option.processType==='variation')&&!this.selectedOptions()[option.id])return `${option.title||option.name} seçimi zorunlu.`}
    for(const field of item.customization||[]){
      const value=this.customValues()[field.formKey];
      const values=Array.isArray(value)?value:(value&&typeof value==='object'?Object.values(value as Record<string,unknown>).filter(Boolean):[]);
      const empty=Array.isArray(value)?value.length===0:(value&&typeof value==='object'?values.length===0:String(value??'').trim()==='');
      if(field.required&&empty)return `${field.title} alanı zorunlu.`;
      const length=Array.isArray(value)?value.length:String(value??'').trim().length;
      const minLength = Number((field as any).minLength ?? (field as any).minlength ?? (field as any).minlenght ?? 0);
      const maxLength = Number((field as any).maxLength ?? (field as any).maxlength ?? (field as any).maxlenght ?? 0);
      if(!empty&&minLength>0&&length<minLength)return `${field.title} en az ${minLength} karakter veya dosya olmalı.`;
      if(!empty&&maxLength>0&&length>maxLength)return `${field.title} en fazla ${maxLength} karakter veya dosya olabilir.`;
    }
    return '';
  }

  private openGuidedRequiredPurchaseFields(item:FortuneListingDetail,gateway:FortunePaymentGateway|null):boolean{
    const fields=(item.customization||[]).filter(field=>{
      if(!field.required)return false;
      const value=this.customValues()[field.formKey];
      if(Array.isArray(value))return value.length===0;
      if(value&&typeof value==='object')return !Object.values(value as Record<string,unknown>).some(Boolean);
      return String(value??'').trim()==='';
    });
    if(!fields.length)return false;
    window.dispatchEvent(new CustomEvent('aymudo-guided-purchase',{detail:{
      stage:'fields',
      title:item.title,
      fields,
      values:{...this.customValues()},
      continuePurchase:async(values:Record<string,unknown>)=>{
        this.customValues.set({...this.customValues(),...values});
        await this.executeFortune(item,gateway);
      }
    }}));
    return true;
  }

  private apiErrorMessage(error: unknown): string {
    if (!error || typeof error !== 'object') {
      return '';
    }
    const value = error as Record<string, unknown>;
    const payload = value['error'];
    if (payload && typeof payload === 'object' && !Array.isArray(payload)) {
      const message = String((payload as Record<string, unknown>)['message'] ?? '').trim();
      if (message) {
        return message;
      }
      if (this.hasRequiredError(payload)) {
        return 'Zorunlu ürün bilgilerini eksiksiz doldur.';
      }
    }
    return String(value['message'] ?? '').trim();
  }

  private hasRequiredError(value: unknown): boolean {
    if (Array.isArray(value)) {
      return value.some((item) => this.hasRequiredError(item));
    }
    if (!value || typeof value !== 'object') {
      return String(value ?? '').toLowerCase() === 'required';
    }
    return Object.values(value as Record<string, unknown>).some((item) => this.hasRequiredError(item));
  }

  protected async openSellerConversation(item: FortuneListingDetail): Promise<void> {
    if (!this.auth.loggedIn()) {
      this.loginPromptOpen.set(true);
      return;
    }
    this.sellerMessageDrawerOpen.set(true);
    // The composer must be usable immediately. An empty thread is created by
    // the first outgoing message; opening the drawer must not depend on a
    // preliminary API request succeeding.
    this.sellerConversationOpen.set(true);
    this.activeSellerThreadID.set(0);
    this.activeSellerName.set(item.sellerName || '');
    this.sellerConversation.set([]);
    this.sellerMessageContextListingID.set(item.id);
    this.sellerMessageContextListingVersionID.set(item.versionID || null);
    this.messageNotice.set('Konuşma yükleniyor...');
    try {
      // Resolve the conversation from the listing itself. Some listing detail
      // payloads do not expose sellerID, while this endpoint can safely derive
      // it from listingID and returns the existing history without sending a
      // placeholder message.
      const result = await firstValueFrom(this.api.post<Record<string, unknown>>('sellerMessageThread', {
        listingID: item.id,
        listingVersionID: item.versionID || undefined,
        sellerID: item.sellerID || undefined,
        message: ''
      }));
      this.applySellerThreadResponse(result);
      this.activeSellerThreadID.set(Number((result['data'] as Record<string, unknown> | undefined)?.['threadID'] ?? 0) || 0);
      this.messageNotice.set('');
      this.scrollSellerConversationToBottom();
    } catch {
      this.sellerConversation.set([]);
      this.activeSellerThreadID.set(0);
      this.messageNotice.set('Yeni mesajını aşağıya yazabilirsin.');
    }
  }

  protected async openSellerThread(threadID: number): Promise<void> {
    const item = this.detail();
    if (!item || threadID <= 0) return;
    this.messageNotice.set('Konuşma yükleniyor...');
    try {
      const result = await firstValueFrom(this.api.post<Record<string, unknown>>('sellerMessageThreads', { threadID }));
      const data = (result['data'] ?? {}) as Record<string, unknown>;
      const selected = Array.isArray(data['threadList'])
        ? (data['threadList'] as Record<string, unknown>[]).find((thread) => Number(thread['id'] ?? 0) === threadID)
        : undefined;
      this.activeSellerName.set(String(selected?.['sellerName'] ?? item.sellerName ?? ''));
      this.activeSellerThreadID.set(threadID);
      this.sellerMessageContextListingID.set(null);
      this.sellerMessageContextListingVersionID.set(null);
      this.applySellerThreadResponse(result);
      this.sellerConversationOpen.set(true);
      this.messageNotice.set('');
      this.scrollSellerConversationToBottom();
    } catch {
      this.activeSellerThreadID.set(0);
      this.messageNotice.set('Konuşma yüklenemedi.');
    }
  }

  protected async showSellerThreads(): Promise<void> {
    this.sellerConversationOpen.set(false);
    this.activeSellerThreadID.set(0);
    this.sellerMessage = '';
    this.clearSellerMessageMedia();
    this.sellerConversation.set([]);
    this.activeSellerName.set('');
    this.sellerMessageContextListingID.set(null);
    this.sellerMessageContextListingVersionID.set(null);
    this.messageNotice.set('');
    try {
      const result = await firstValueFrom(this.api.post<Record<string, unknown>>('sellerMessageThreads', {}));
      this.applySellerThreadResponse(result);
    } catch {
      this.messageNotice.set('Mesajlar yüklenemedi.');
    }
  }

  private applySellerThreadResponse(response: Record<string, unknown>): void {
    const data = (response['data'] ?? {}) as Record<string, unknown>;
    const threads = Array.isArray(data['threadList']) ? data['threadList'] as Record<string, unknown>[] : [];
    this.sellerThreadList.set(threads.map((thread) => ({
      id: Number(thread['id'] ?? 0),
      sellerID: Number(thread['relatedID'] ?? 0) || undefined,
      sellerName: String(thread['sellerName'] ?? thread['sellerShopName'] ?? 'Falcı'),
      sellerAvatar: thread['sellerAvatar'] ? String(thread['sellerAvatar']) : null,
      sellerAvatarSizes: (thread['sellerAvatarSizes'] as Record<string, unknown> | unknown[] | null) ?? null,
      lastMessage: thread['lastMessage'] ? String(thread['lastMessage']) : null,
      lastMessageAt: thread['lastMessageAt'] ? String(thread['lastMessageAt']) : null,
      hasUnread: Boolean(thread['hasUnread'] ?? Number(thread['unreadCount'] ?? 0) > 0),
      threadStatusLabel: thread['threadStatusLabel'] ? String(thread['threadStatusLabel']) : null
    })));
    const messages = Array.isArray(data['messages']) ? data['messages'] as Record<string, unknown>[] : [];
    this.sellerConversation.set(messages.map((message) => ({
      text: String(message['message'] ?? ''),
      mine: String(message['senderType'] ?? '').toLowerCase() === 'user',
      mediaID: Number(message['mediaID'] ?? 0) || null,
      threadFileID: Number(message['threadFileID'] ?? 0) || null,
      mediaUrl: message['mediaUrl'] ? String(message['mediaUrl']) : null,
      fileUrl: message['fileUrl'] ? String(message['fileUrl']) : null,
      fileName: message['fileName'] ? String(message['fileName']) : null,
      mimeType: message['mimeType'] ? String(message['mimeType']) : null,
      isImage: Boolean(Number(message['isImage'] ?? 0)),
      attachments: Array.isArray(message['attachments'])
        ? (message['attachments'] as Record<string, unknown>[]).map((attachment) => ({
            threadFileID: Number(attachment['threadFileID'] ?? 0),
            fileName: attachment['fileName'] ? String(attachment['fileName']) : null,
            mimeType: attachment['mimeType'] ? String(attachment['mimeType']) : null,
            extension: attachment['extension'] ? String(attachment['extension']) : null,
            isImage: Number(attachment['isImage'] ?? 0)
          }))
        : [],
      listingSlug: message['listingSlug'] ? String(message['listingSlug']) : null,
      listingTitle: message['listingTitle'] ? String(message['listingTitle']) : null,
      listingImageUrl: message['listingImageUrl'] ? String(message['listingImageUrl']) : null
    })));
    void this.preloadThreadImageAttachments();
    this.scrollSellerConversationToBottom();
  }

  protected async uploadSellerMessageImage(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const files = Array.from(input.files ?? []);
    if (!files.length) return;
    this.sellerMessageUploading.set(true);
    this.messageNotice.set('Dosya yükleniyor...');
    try {
      const current = [...this.sellerMessageUploads()];
      for (const file of files) {
        if (current.length >= 6) {
          break;
        }
        if (file.size > 20 * 1024 * 1024) {
          continue;
        }
        const body = new FormData();
        body.append('snpFileUpload', file);
        body.append('lastModified', String(file.lastModified || Date.now()));
        const response = await firstValueFrom(this.api.postFormData<Record<string, unknown>>('threadFileUpload', body));
        const uploadID = this.findNumericValue(response, ['uploadID', 'id']);
        if (!uploadID) {
          continue;
        }
        current.push({
          uploadID,
          fileName: file.name || 'dosya',
          isImage: file.type.startsWith('image/'),
          previewUrl: file.type.startsWith('image/') ? URL.createObjectURL(file) : null
        });
      }
      this.sellerMessageUploads.set(current);
      this.messageNotice.set('Dosya mesaja eklendi.');
    } catch {
      this.messageNotice.set('Dosya yüklenemedi. Lütfen tekrar dene.');
    } finally {
      this.sellerMessageUploading.set(false);
      input.value = '';
    }
  }

  private findNumericValue(value: unknown, keys: string[]): number {
    if (Array.isArray(value)) {
      for (const item of value) {
        const found = this.findNumericValue(item, keys);
        if (found) return found;
      }
      return 0;
    }
    if (!value || typeof value !== 'object') return 0;
    const record = value as Record<string, unknown>;
    for (const key of keys) {
      const number = Number(record[key] ?? 0);
      if (number > 0) return number;
    }
    for (const child of Object.values(record)) {
      const found = this.findNumericValue(child, keys);
      if (found) return found;
    }
    return 0;
  }

  protected async sendSellerMessage(item: FortuneListingDetail): Promise<void> {
    const typedMessage = this.sellerMessage.trim();
    const uploads = this.sellerMessageUploads();
    if (!typedMessage && !uploads.length) {
      return;
    }
    const message = typedMessage || 'Dosya gönderildi.';
    this.messageSending.set(true);
    this.messageNotice.set('');
    try {
      const activeThreadID = this.activeSellerThreadID();
      const payload = activeThreadID > 0
        ? {
            threadID: activeThreadID,
            message,
            uploadIDs: uploads.map((entry) => entry.uploadID),
            listingID: this.sellerMessageContextListingID() || undefined,
            listingVersionID: this.sellerMessageContextListingVersionID() || undefined
          }
        : {
            listingID: item.id,
            listingVersionID: item.versionID || undefined,
            sellerID: item.sellerID,
            message,
            uploadIDs: uploads.map((entry) => entry.uploadID)
          };
      const response = await firstValueFrom(this.api.post<Record<string, unknown>>('sellerMessageThread', payload));
      const data = (response['data'] ?? {}) as Record<string, unknown>;
      const returnedThreadID = Number(data['threadID'] ?? 0) || activeThreadID;
      if (returnedThreadID > 0) {
        this.activeSellerThreadID.set(returnedThreadID);
      }
      const messages = Array.isArray(data['messages']) ? data['messages'] as Record<string, unknown>[] : [];
      if (messages.length) {
        const localPreviewByName = new Map<string, string>();
        for (const upload of uploads) {
          if (upload.isImage && upload.previewUrl) {
            localPreviewByName.set(String(upload.fileName || '').trim().toLowerCase(), upload.previewUrl);
          }
        }
        const conversation = messages.map((item) => ({
          text: String(item['message'] ?? ''),
          mine: String(item['senderType'] ?? '').toLowerCase() === 'user',
          mediaID: Number(item['mediaID'] ?? 0) || null,
          threadFileID: Number(item['threadFileID'] ?? 0) || null,
          mediaUrl: item['mediaUrl'] ? String(item['mediaUrl']) : null,
          fileUrl: item['fileUrl'] ? String(item['fileUrl']) : null,
          fileName: item['fileName'] ? String(item['fileName']) : null,
          mimeType: item['mimeType'] ? String(item['mimeType']) : null,
          isImage: Boolean(Number(item['isImage'] ?? 0)),
          attachments: Array.isArray(item['attachments'])
            ? (item['attachments'] as Record<string, unknown>[]).map((attachment) => ({
                threadFileID: Number(attachment['threadFileID'] ?? 0),
                fileName: attachment['fileName'] ? String(attachment['fileName']) : null,
                mimeType: attachment['mimeType'] ? String(attachment['mimeType']) : null,
                extension: attachment['extension'] ? String(attachment['extension']) : null,
                isImage: Number(attachment['isImage'] ?? 0)
              }))
            : [],
          listingSlug: item['listingSlug'] ? String(item['listingSlug']) : null,
          listingTitle: item['listingTitle'] ? String(item['listingTitle']) : null,
          listingImageUrl: item['listingImageUrl'] ? String(item['listingImageUrl']) : null
        }));
        const previewMap = { ...this.threadFilePreviewUrls() };
        for (const messageItem of conversation) {
          for (const attachment of messageItem.attachments ?? []) {
            const threadFileID = Number(attachment.threadFileID ?? 0);
            if (threadFileID <= 0 || previewMap[threadFileID] || !this.attachmentIsImage(attachment)) {
              continue;
            }
            const localPreview = localPreviewByName.get(String(attachment.fileName ?? '').trim().toLowerCase());
            if (localPreview) {
              previewMap[threadFileID] = localPreview;
            }
          }
        }
        this.threadFilePreviewUrls.set(previewMap);
        this.sellerConversation.set(conversation);
        void this.preloadThreadImageAttachments();
      } else {
        this.sellerConversation.update((items) => [...items, {
          text: message,
          mine: true,
          mediaID: null,
          attachments: uploads.map((entry, index) => ({
            threadFileID: -(index + 1),
            fileName: entry.fileName,
            mimeType: null,
            extension: entry.fileName.split('.').pop() ?? null,
            isImage: entry.isImage ? 1 : 0
          })),
          listingSlug: item.slug,
          listingTitle: item.title,
          listingImageUrl: this.heroImage()?.url ?? item.imageUrl ?? null
        }]);
      }
      this.sellerConversationOpen.set(true);
      this.sellerMessage = '';
      this.clearSellerMessageMedia(false);
      this.sellerMessageContextListingID.set(null);
      this.sellerMessageContextListingVersionID.set(null);
      this.messageNotice.set('');
      this.scrollSellerConversationToBottom();
    } catch {
      this.messageNotice.set(this.auth.loggedIn() ? 'Mesaj gönderilemedi. Lütfen tekrar dene.' : 'Mesaj göndermek için giriş yapman gerekiyor.');
    } finally {
      this.messageSending.set(false);
    }
  }

  private async load(slug: string, hasSeededDetail = false): Promise<void> {
    const sequence = ++this.loadSequence;
    if (!hasSeededDetail) {
      this.loading.set(true);
      this.detail.set(null);
    }
    this.loadError.set('');
    try {
      const detail = await this.fortune.listingDetail(slug, hasSeededDetail, this.route.snapshot.queryParamMap.get('offer') ?? '');
      const activeSlug = this.route.snapshot.paramMap.get('slug') ?? '';
      if (sequence !== this.loadSequence || activeSlug !== slug) {
        return;
      }
      this.detail.set(detail);
      this.activeImageIndex.set(0);
      this.sameCategoryListings.set([]);
      this.sameSellerListings.set([]);
      if (detail) {
        const schemaDescription = detail.description
          .replace(/<[^>]*>/g, ' ')
          .replace(/!\[[^\]]*\]\([^)]*\)/g, ' ')
          .replace(/\[([^\]]+)\]\([^)]*\)/g, '$1')
          .replace(/^\s{0,3}#{1,6}\s+/gm, '')
          .replace(/[*_~`>]+/g, '')
          .replace(/^\s*[-+]\s+/gm, '')
          .replace(/\s+/g, ' ')
          .trim().slice(0, 260) || this.localizedDescription(detail);
        const schemaBrand = this.sellerLabel(detail).trim() || this.site.config().name;
        const schemaRatedReviews = detail.reviews
          .filter((review) => !this.isEditorialReview(review) && Number(review['rating'] || review['score'] || 0) > 0)
          .map((review) => ({
            review,
            rating: Math.max(1, Math.min(5, Number(review['rating'] || review['score'] || 0))),
            body: String(review['review'] || review['comment'] || review['content'] || review['description'] || '').replace(/<[^>]*>/g, '').trim()
          }));
        const schemaRatingValues = schemaRatedReviews.map((item) => item.rating);
        const schemaRatingAverage = schemaRatingValues.length
          ? Number((schemaRatingValues.reduce((sum, value) => sum + value, 0) / schemaRatingValues.length).toFixed(2))
          : 0;
        const schemaReviewCount = Math.max(1, Number(detail.reviewCount || schemaRatingValues.length || 0));
        const schemaAggregateRating = detail.rating > 0 && detail.reviewCount > 0
          ? Number(detail.rating)
          : schemaRatingAverage;
        this.analytics.identifyPage('listing', detail.id, detail.title, detail.versionID ?? undefined);
        const related = await this.fortune.relatedListings(detail);
        this.sameCategoryListings.set(related.sameCategory);
        this.sameSellerListings.set(related.sameSeller);
        this.seo.setPage({
          title: detail.title,
          description: schemaDescription.slice(0, 160),
          image: detail.images[0]?.url ?? detail.imageUrl,
          path: this.i18n.routePath('listing', { slug: detail.slug }),
          type: 'product',
          price: detail.seoPrice ?? this.totalPrice(),
          currency: detail.seoCurrency ?? (detail.currency === 'CREDIT' ? 'TRY' : detail.currency),
          availability: 'https://schema.org/InStock',
          brand: schemaBrand,
          breadcrumbs: [
            { name: this.i18n.t('home'), path: this.i18n.routePath('home') },
            { name: this.i18n.t('getFortune'), path: this.i18n.routePath('fortune') },
            ...(detail.categoryTitle ? [{
              name: detail.categoryTitle,
              path: detail.categorySlug
                ? this.i18n.routePath('category', { slug: detail.categorySlug })
                : this.i18n.routePath('fortune')
            }] : []),
            { name: detail.title, path: this.i18n.routePath('listing', { slug: detail.slug }) }
          ],
          schema: {
            '@type': 'Product',
            sku: String(detail.id),
            name: detail.title,
            description: schemaDescription,
            category: detail.categoryTitle || undefined,
            brand: { '@type': 'Brand', name: schemaBrand },
            ...(this.site.config().brandKey === 'bdsmbeds'
              ? { hasAdultConsideration: 'https://schema.org/SexualContentConsideration' }
              : {}),
            ...(schemaAggregateRating > 0 && schemaReviewCount > 0 ? {
              aggregateRating: {
                '@type': 'AggregateRating',
                ratingValue: schemaAggregateRating,
                reviewCount: schemaReviewCount,
                bestRating: 5,
                worstRating: 1
              }
            } : {}),
            ...(schemaRatedReviews.some((item) => item.body) ? {
              review: schemaRatedReviews.filter((item) => item.body).slice(0, 10).map(({ review, rating, body }) => ({
                '@type': 'Review',
                author: { '@type': 'Person', name: this.reviewSchemaAuthorName(review) },
                reviewBody: body,
                reviewRating: {
                  '@type': 'Rating',
                  ratingValue: rating,
                  bestRating: 5,
                  worstRating: 1
                }
              }))
            } : {}),
            offers: {
              '@type': 'Offer',
              price: detail.seoPrice ?? this.totalPrice(),
              priceCurrency: detail.seoCurrency ?? (detail.currency === 'CREDIT' ? 'TRY' : detail.currency),
              availability: 'https://schema.org/InStock',
              url: `${this.site.config().apiBaseUrl.replace(/\/+$/, '')}${this.i18n.routePath('listing', { slug: detail.slug })}`,
              seller: { '@type': 'Organization', name: schemaBrand },
              hasMerchantReturnPolicy: this.merchantReturnPolicy(detail),
              ...(detail.listingType === 'physical' ? { shippingDetails: this.offerShippingDetails(detail) } : {})
            }
          }
        });
      }
      const defaults: Record<number, number> = {};
      const customDefaults: Record<string, unknown> = {};
      for (const [optionIndex, option] of (detail?.options ?? []).entries()) {
        const firstActive = option.subOptions.find((sub) => (sub as { active?: boolean }).active !== false);
        if ((option.required || option.processType === 'variation') && firstActive) {
          defaults[option.id || optionIndex] = firstActive.id;
        }
      }
      for (const field of detail?.customization ?? []) {
        customDefaults[field.formKey] = this.isUploadField(field.type) ? [] : '';
      }
      this.selectedOptions.set({ ...defaults, ...(this.sharedOptionValues ?? {}) });
      this.customValues.set(customDefaults);
      if (!detail) {
        this.loadError.set(this.copy().notFound);
        this.seo.setNotFound(this.copy().notFound, this.commerce() ? 'Aradığın ürün bulunamadı veya artık yayında değil.' : 'Aradığın fal hizmeti bulunamadı veya artık yayında değil.');
      }
    } catch {
      this.loadError.set(this.copy().notFound);
      this.seo.setNotFound(this.copy().notFound, this.commerce() ? 'Aradığın ürün bulunamadı veya artık yayında değil.' : 'Aradığın fal hizmeti bulunamadı veya artık yayında değil.');
    } finally {
      this.loading.set(false);
    }
  }

  private merchantReturnPolicy(detail: FortuneListingDetail): Record<string, unknown> {
    const acceptedValue = this.policyValue(detail, ['returnsAccepted', 'returnAccepted', 'returns_accepted']);
    const daysValue = this.policyValue(detail, ['returnDays', 'return_days', 'days']);
    const feeOwner = this.returnShippingCustomer(detail)
      ?? String(detail.laws['returnShippingFees'] ?? detail.laws['returnShippingCost'] ?? 'customer').toLowerCase();
    const accepted = !['0', 'false', 'no'].includes(String(acceptedValue ?? detail.laws['returnsAccepted'] ?? 'true').toLowerCase());
    const days = Math.max(0, Number(daysValue ?? detail.laws['returnDays'] ?? detail.laws['returnPeriod'] ?? 14));
    return {
      '@type': 'MerchantReturnPolicy',
      applicableCountry: this.i18n.country(),
      returnPolicyCategory: accepted
        ? 'https://schema.org/MerchantReturnFiniteReturnWindow'
        : 'https://schema.org/MerchantReturnNotPermitted',
      ...(accepted ? {
        merchantReturnDays: days,
        returnMethod: 'https://schema.org/ReturnByMail',
        returnFees: feeOwner === 'seller'
          ? 'https://schema.org/FreeReturn'
          : 'https://schema.org/ReturnShippingFees'
      } : {})
    };
  }

  private encodeSharedOptions(options: Record<number, number>): string {
    const compact = Object.fromEntries(Object.entries(options).filter(([, value]) => Number(value) > 0));
    const json = JSON.stringify(compact);
    if (typeof window === 'undefined') return encodeURIComponent(json);
    const binary = btoa(encodeURIComponent(json).replace(/%([0-9A-F]{2})/g, (_, code: string) => String.fromCharCode(parseInt(code, 16))));
    return binary.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  }

  private decodeSharedOptions(value: string | null): Record<number, number> | null {
    if (!value || typeof atob !== 'function') return null;
    try {
      const normalized = value.replace(/-/g, '+').replace(/_/g, '/');
      const padded = normalized + '='.repeat((4 - normalized.length % 4) % 4);
      const decoded = decodeURIComponent(atob(padded).split('').map((char) => `%${char.charCodeAt(0).toString(16).padStart(2, '0')}`).join(''));
      const parsed: unknown = JSON.parse(decoded);
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null;
      return Object.fromEntries(Object.entries(parsed).filter(([key, optionID]) => /^\d+$/.test(key) && Number(optionID) > 0).map(([key, optionID]) => [Number(key), Number(optionID)]));
    } catch {
      return null;
    }
  }

  private offerShippingDetails(detail: FortuneListingDetail): Record<string, unknown> {
    const raw = this.shippingOptions(detail)[0] ?? detail.shippingDetails ?? {};
    const currency = String(raw['currency'] ?? detail.currency);
    const handlingMin = Number(raw['minShipping'] ?? raw['handlingDays'] ?? 0);
    const handlingMax = Number(raw['maxShipping'] ?? raw['handlingDays'] ?? handlingMin);
    const period = String(raw['shippingPeriod'] ?? '').toLowerCase();
    const handlingMultiplier = period.includes('week') ? 7 : period.includes('month') ? 30 : 1;
    return {
      '@type': 'OfferShippingDetails',
      shippingDestination: { '@type': 'DefinedRegion', addressCountry: this.i18n.country() },
      shippingRate: {
        '@type': 'MonetaryAmount',
        value: Math.max(0, Number(raw['onePrice'] ?? raw['price'] ?? raw['shippingPrice'] ?? raw['amount'] ?? 0)),
        currency
      },
      deliveryTime: {
        '@type': 'ShippingDeliveryTime',
        handlingTime: { '@type': 'QuantitativeValue', minValue: handlingMin * handlingMultiplier, maxValue: handlingMax * handlingMultiplier, unitCode: 'DAY' },
        transitTime: { '@type': 'QuantitativeValue', minValue: Number(raw['minDay'] ?? raw['minDays'] ?? 1), maxValue: Number(raw['maxDay'] ?? raw['maxDays'] ?? 7), unitCode: 'DAY' }
      }
    };
  }

  protected initials(name: string): string {
    return name.split(' ').filter(Boolean).slice(0, 2).map((part) => part[0]?.toUpperCase()).join('') || 'FD';
  }

  protected uploadUrl(field: { type: string }): string {
    // All form image fields use the shared form uploader. This keeps the
    // free-reading form and product-intake forms on the same API and storage
    // contract; only the legacy video field keeps its dedicated endpoint.
    const path = field.type === 'video' ? '/upload/fortune-video' : '/upload/form';
    if (typeof window === 'undefined' || window.location.hostname.startsWith('next.')) {
      return path;
    }
    return `${this.site.config().apiBaseUrl}${path}`;
  }

  private findResponseValue(value: unknown, key: string): unknown {
    if (!value || typeof value !== 'object') {
      return undefined;
    }
    if (!Array.isArray(value) && Object.prototype.hasOwnProperty.call(value, key)) {
      return (value as Record<string, unknown>)[key];
    }
    for (const child of Object.values(value as Record<string, unknown>)) {
      const found = this.findResponseValue(child, key);
      if (found !== undefined) {
        return found;
      }
    }
    return undefined;
  }

  protected clearSellerMessageMedia(revokeObjectUrls = true): void {
    for (const upload of this.sellerMessageUploads()) {
      if (revokeObjectUrls && upload.previewUrl && upload.previewUrl.startsWith('blob:') && typeof URL !== 'undefined') {
        URL.revokeObjectURL(upload.previewUrl);
      }
    }
    this.sellerMessageUploads.set([]);
  }

  protected threadFilePreviewUrl(threadFileID: number | null | undefined): string | null {
    const id = Number(threadFileID ?? 0);
    if (id < 0) {
      const preview = this.sellerMessageUploads()[Math.abs(id) - 1]?.previewUrl ?? null;
      return preview;
    }
    return id > 0 ? this.threadFilePreviewUrls()[id] ?? null : null;
  }

  private async preloadThreadImageAttachments(): Promise<void> {
    const next = { ...this.threadFilePreviewUrls() };
    for (const message of this.sellerConversation()) {
      for (const attachment of message.attachments ?? []) {
        const threadFileID = Number(attachment.threadFileID ?? 0);
        if (!this.attachmentIsImage(attachment) || threadFileID <= 0 || next[threadFileID]) {
          continue;
        }
        try {
          const blob = await firstValueFrom(this.api.postBlob('threadFileDownload', { threadFileID }));
          next[threadFileID] = URL.createObjectURL(blob);
        } catch {
          continue;
        }
      }
    }
    this.threadFilePreviewUrls.set(next);
  }

  protected async downloadThreadFile(message: {
    threadFileID?: number | null;
    fileName?: string | null;
  }): Promise<void> {
    const threadFileID = Number(message.threadFileID ?? 0);
    if (threadFileID <= 0) {
      return;
    }
    try {
      const blob = await firstValueFrom(this.api.postBlob('threadFileDownload', { threadFileID }));
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = String(message.fileName || 'mesaj-dosyasi');
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
    } catch {
      this.messageNotice.set('Dosya indirilemedi.');
    }
  }

  private scrollSellerConversationToBottom(): void {
    if (typeof window === 'undefined') {
      return;
    }
    window.requestAnimationFrame(() => {
      const element = document.querySelector('.seller-message-history') as HTMLElement | null;
      if (element) {
        element.scrollTop = element.scrollHeight;
      }
    });
  }

  protected isEditorialReview(review: Record<string, unknown>): boolean {
    return Number(review['isEditorial'] ?? 0) === 1;
  }

  protected showCategoryBreadcrumb(categoryTitle: string | null | undefined): boolean {
    const normalize = (value: unknown) => String(value || '').trim().toLocaleLowerCase(this.i18n.lang());
    const category = normalize(categoryTitle);
    const root = normalize(this.commerce() ? this.copy().products : this.copy().reading);
    return category !== '' && category !== root;
  }

  protected reviewDisplayName(review: Record<string, unknown>): string {
    const name = String(review['reviewerName'] || review['userName'] || review['name'] || '').trim();
    if (this.reviewShowsName(review)) return name || this.reviewerFallback();
    if (name.includes('*')) return name;
    const parts = name.split(/\s+/).filter(Boolean);
    if (!parts.length) return 'M*****';
    const first = `${parts[0].charAt(0).toLocaleUpperCase(this.i18n.lang())}*****`;
    return parts.length > 1 ? `${first} ${parts.at(-1)!.charAt(0).toLocaleUpperCase(this.i18n.lang())}****` : first;
  }

  private reviewSchemaAuthorName(review: Record<string, unknown>): string {
    if (!this.reviewShowsName(review)) return 'Müşteri';
    return String(review['reviewerName'] || review['userName'] || review['name'] || 'Müşteri');
  }

  private reviewShowsName(review: Record<string, unknown>): boolean {
    const value = review['showName'];
    return value === true || value === 1 || value === '1';
  }

  protected reviewStars(review: Record<string, unknown>): string {
    const rating = Math.max(0, Math.min(5, Math.round(Number(review['rating'] ?? review['score'] ?? 0))));
    return `${'★'.repeat(rating)}${'☆'.repeat(5 - rating)}`;
  }

  protected editorialLabel(): string {
    return ({ tr: 'Editoryal içerik', en: 'Editorial content', de: 'Redaktioneller Inhalt', es: 'Contenido editorial', fr: 'Contenu éditorial' } as Record<string, string>)[this.i18n.lang()] || 'Editorial content';
  }

  protected reviewsHeading(): string {
    const commerce = this.commerce();
    const labels: Record<string, string[]> = {
      tr: ['Ürün yorumları', 'Fal hakkında içerikler'], en: ['Product reviews', 'Reading insights'], de: ['Produktbewertungen', 'Inhalte zur Beratung'], es: ['Reseñas del producto', 'Contenido sobre la lectura'], fr: ['Avis produit', 'Contenus sur la consultation']
    };
    return (labels[this.i18n.lang()] || labels['en'])[commerce ? 0 : 1];
  }

  protected reviewerFallback(): string {
    return ({ tr: 'İçerik ekibi', en: 'Editorial team', de: 'Redaktion', es: 'Equipo editorial', fr: 'Équipe éditoriale' } as Record<string, string>)[this.i18n.lang()] || 'Editorial team';
  }

  protected normalizeAssetUrl(url: string | null | undefined): string {
    const value = String(url ?? '').trim();
    if (!value) {
      return '';
    }
    if (/^(blob:|data:)/.test(value)) {
      return value;
    }
    const prepared = value.includes('[size]') ? value.replace('[size]', 'm') : value;
    const mediaBaseUrl = String(this.site.config().mediaBaseUrl ?? '').replace(/\/+$/, '');
    if (/^https?:\/\//.test(prepared)) {
      try {
        const parsed = new URL(prepared);
        return mediaBaseUrl && parsed.pathname.startsWith('/media/')
          ? `${mediaBaseUrl}${parsed.pathname}${parsed.search}${parsed.hash}`
          : prepared;
      } catch {
        return prepared;
      }
    }
    const path = `/${prepared.replace(/^\/+/, '')}`;
    if (mediaBaseUrl && path.startsWith('/media/')) {
      return `${mediaBaseUrl}${path}`;
    }
    return `${String(this.site.config().apiBaseUrl ?? '').replace(/\/+$/, '')}${path}`;
  }
}
